import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    LinearProgress,
    Alert,
    Modal,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Divider,
    Tooltip,
    Grid,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Drawer,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import {
    Add,
    Edit,
    Delete,
    Logout,
    BugReport,
    CloudUpload,
    Close,
    Save,
    Warning,
    AutoAwesome,
    FileDownload,
    FileUpload,
    TableChart,
    CheckCircle,
    Menu as MenuIcon,
    ArrowUpward,
    ArrowDownward,
    ChevronLeft,
    ChevronRight,
    Visibility,
    VisibilityOff,
} from "@mui/icons-material"
import { getCardArtUrl, getDeckBackUrl } from "../utils/cardArtwork"
import { motion, useAnimation } from "framer-motion"
import { D10Icon } from "../components/icons"
import {
    getNavConfig,
    saveNavConfig,
    getWhitepaperVersion,
    bumpWhitepaperVersion,
} from "../utils/rulesClient"
import { exportWhitepapers, CURRENT_VERSION } from "../utils/exportWhitepapers"

// ═══════════════════════════════════════════════════════════
// Configuration — set these env vars after deploying CDK:
//   REACT_APP_COGNITO_USER_POOL_ID
//   REACT_APP_COGNITO_CLIENT_ID
//   REACT_APP_AWS_REGION
// ═══════════════════════════════════════════════════════════
const AWS_REGION = process.env.REACT_APP_AWS_REGION || "us-east-1"
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || ""
const API_URL = (
    process.env.REACT_APP_CARDS_API_URL ||
    "https://6tnku32a8f.execute-api.us-east-1.amazonaws.com"
).replace(/\/$/, "")

// ── Cognito direct-API helpers ────────────────────────────────────────────────
async function cognitoCall(target, body) {
    const res = await fetch(
        `https://cognito-idp.${AWS_REGION}.amazonaws.com/`,
        {
            method: "POST",
            headers: {
                "X-Amz-Target": `AWSCognitoIdentityProviderService.${target}`,
                "Content-Type": "application/x-amz-json-1.1",
            },
            body: JSON.stringify(body),
        },
    )
    return res.json()
}

async function cognitoSignIn(email, password) {
    const data = await cognitoCall("InitiateAuth", {
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: CLIENT_ID,
        AuthParameters: { USERNAME: email, PASSWORD: password },
    })
    if (data.__type === "NotAuthorizedException")
        throw new Error("Incorrect username or password.")
    if (data.__type === "UserNotFoundException")
        throw new Error("No account found for this email.")
    if (data.__type) throw new Error(data.message || data.__type)
    if (data.ChallengeName === "NEW_PASSWORD_REQUIRED") {
        return { type: "newPasswordRequired", session: data.Session }
    }
    return { type: "success", tokens: data.AuthenticationResult }
}

async function cognitoNewPassword(email, session, newPassword) {
    const data = await cognitoCall("RespondToAuthChallenge", {
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        ClientId: CLIENT_ID,
        Session: session,
        ChallengeResponses: { USERNAME: email, NEW_PASSWORD: newPassword },
    })
    if (data.__type) throw new Error(data.message || data.__type)
    return data.AuthenticationResult
}

async function cognitoSignOut(accessToken) {
    await cognitoCall("GlobalSignOut", { AccessToken: accessToken }).catch(
        () => {},
    )
}

// ── Admin API helpers ─────────────────────────────────────────────────────────
function adminFetch(path, method = "GET", body) {
    const idToken = localStorage.getItem("imk_admin_id_token")
    return fetch(`${API_URL}${path}`, {
        method,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    }).then(async (r) => {
        const json = await r.json()
        if (!r.ok) throw new Error(json.error || `HTTP ${r.status}`)
        return json
    })
}

// ── Rarity colours matching the game ──────────────────────────────────────────
const RARITY_COLORS = {
    common: "#777",
    uncommon: "#2e7d32",
    rare: "#1565c0",
    mythic: "#8e24aa",
}

const POWER_RARITIES = ["common", "uncommon", "rare", "mythic"]

// ── Equipment constants ───────────────────────────────────────────────────────
const SUBCATEGORY_COLORS = {
    Weapon: "#8B0000",
    Protection: "#1565c0",
    Control: "#2e7d32",
    Denial: "#6a1b9a",
}
const SUBCATEGORY_ORDER = ["Weapon", "Protection", "Control", "Denial"]
const TIER_OPTIONS = ["Low Pay", "High Pay"]
const ITEM_DEFAULTS = {
    name: "",
    subcategory: "Weapon",
    tier: "Low Pay",
    description: "",
    damageType: "",
    range: "",
    protects: "",
    uses: "",
    trick: "",
}

// ── CSV helpers ───────────────────────────────────────────────────────────
const POWER_CSV_COLS = ["name", "rarity", "description"]
const MONSTER_CSV_COLS = [
    "name",
    "Description",
    "Guise",
    "Attack",
    "Damage",
    "Hit Points Multiplier",
    "Bloodied",
    "Buffs",
    "Crit",
    "Immunities",
    "Special Weaknesses",
    "Body",
    "Agility",
    "Focus",
    "Fate",
    "Insight",
]

const EQUIPMENT_CSV_COLS = [
    "name",
    "subcategory",
    "tier",
    "description",
    "damageType",
    "range",
    "protects",
    "uses",
    "trick",
]

function csvCell(val) {
    const s =
        val === null || val === undefined
            ? ""
            : String(Array.isArray(val) ? val.join(", ") : val)
    return s.includes(",") || s.includes('"') || s.includes("\n")
        ? `"${s.replace(/"/g, '""')}"`
        : s
}

function exportEquipmentAsCsv(items) {
    const cols = EQUIPMENT_CSV_COLS
    const header = cols.join(",")
    const rows = items.map((item) =>
        cols.map((col) => csvCell(item[col])).join(","),
    )
    const csv = [header, ...rows].join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "hunters-arsenal-equipment.csv"
    a.click()
    URL.revokeObjectURL(url)
}

function exportCardsAsCsv(cards, cardType, deckDisplayName) {
    const cols = cardType === "monster" ? MONSTER_CSV_COLS : POWER_CSV_COLS
    const header = cols.join(",")
    const rows = cards.map((c) => cols.map((col) => csvCell(c[col])).join(","))
    const csv = [header, ...rows].join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${deckDisplayName.replace(/[^a-z0-9]/gi, "-").toLowerCase()}-cards.csv`
    a.click()
    URL.revokeObjectURL(url)
}

// Parse a CSV string into an array of objects. Handles quoted fields.
function parseCsv(text) {
    const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n")
    if (lines.length < 2)
        throw new Error("CSV must have a header row and at least one data row.")
    const headers = splitCsvRow(lines[0])
    const rows = []
    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue
        const values = splitCsvRow(line)
        const obj = {}
        headers.forEach((h, idx) => {
            obj[h.trim()] = (values[idx] || "").trim()
        })
        rows.push(obj)
    }
    return { headers, rows }
}

function splitCsvRow(row) {
    const cells = []
    let cur = "",
        inQuote = false
    for (let i = 0; i < row.length; i++) {
        const ch = row[i]
        if (ch === '"') {
            if (inQuote && row[i + 1] === '"') {
                cur += '"'
                i++
            } else inQuote = !inQuote
        } else if (ch === "," && !inQuote) {
            cells.push(cur)
            cur = ""
        } else {
            cur += ch
        }
    }
    cells.push(cur)
    return cells
}

// ═══════════════════════════════════════════════════════════
// Deck Form Modal
// ═══════════════════════════════════════════════════════════
function DeckFormModal({ open, onClose, onSave, initialDeck }) {
    const isEdit = !!initialDeck
    const [form, setForm] = useState({
        deck: "",
        displayName: "",
        description: "",
        cardType: "power",
    })
    const [backImageFile, setBackImageFile] = useState(null)
    const [backImagePreview, setBackImagePreview] = useState("")
    const backFileRef = useRef()
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (open) {
            setBackImageFile(null)
            if (isEdit) {
                setForm({
                    deck: initialDeck.deck,
                    displayName: initialDeck.displayName || initialDeck.deck,
                    description: initialDeck.description || "",
                    cardType: initialDeck.cardType || "power",
                })
                setBackImagePreview(getDeckBackUrl(initialDeck.deck))
            } else {
                setForm({
                    deck: "",
                    displayName: "",
                    description: "",
                    cardType: "power",
                })
                setBackImagePreview("")
            }
            setError("")
        }
    }, [open, initialDeck, isEdit])

    const handleBackImageChange = (e) => {
        const file = e.target.files[0]
        if (!file) return
        setBackImageFile(file)
        setBackImagePreview(URL.createObjectURL(file))
    }

    const handleSave = async () => {
        if (!form.displayName.trim()) {
            setError("Name is required.")
            return
        }
        setSaving(true)
        try {
            const deckId = isEdit
                ? form.deck
                : form.displayName
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")
                      .replace(/^-|-$/g, "")
            // Upload deck back art if provided
            if (backImageFile) {
                const { uploadUrl } = await adminFetch(
                    "/admin/upload-url",
                    "POST",
                    {
                        deck: deckId,
                        name: "back",
                        folder: "backs",
                        contentType: backImageFile.type || "image/png",
                    },
                )
                await fetch(uploadUrl, {
                    method: "PUT",
                    body: backImageFile,
                    headers: {
                        "Content-Type": backImageFile.type || "image/png",
                    },
                })
            }
            await onSave({ ...form, deck: deckId })
            onClose()
        } catch (e) {
            setError(e.message)
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Paper
                sx={{
                    width: { xs: "calc(100vw - 32px)", sm: 480 },
                    maxWidth: 480,
                    borderRadius: "16px",
                    overflow: "hidden",
                    outline: "none",
                }}
            >
                <Box
                    sx={{
                        p: 2.5,
                        bgcolor: "#1a0a0a",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                        }}
                    >
                        {isEdit ? "Edit Deck" : "New Deck / Booster Pack"}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <Close />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {error && <Alert severity='error'>{error}</Alert>}
                    <TextField
                        label='Display Name'
                        value={form.displayName}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                displayName: e.target.value,
                            }))
                        }
                        fullWidth
                        required
                        autoFocus
                        helperText={
                            !isEdit
                                ? `ID: ${
                                      form.displayName
                                          .toLowerCase()
                                          .replace(/[^a-z0-9]+/g, "-")
                                          .replace(/^-|-$/g, "") ||
                                      "auto-generated"
                                  }`
                                : `ID: ${form.deck}`
                        }
                    />
                    <FormControl fullWidth>
                        <InputLabel>Card Type</InputLabel>
                        <Select
                            value={form.cardType}
                            label='Card Type'
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    cardType: e.target.value,
                                }))
                            }
                        >
                            <MenuItem value='power'>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <AutoAwesome
                                        sx={{ fontSize: 16, color: "#8e24aa" }}
                                    />{" "}
                                    Power Cards
                                </Box>
                            </MenuItem>
                            <MenuItem value='monster'>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <BugReport
                                        sx={{ fontSize: 16, color: "#c62828" }}
                                    />{" "}
                                    Monster Cards
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        label='Description'
                        value={form.description}
                        onChange={(e) =>
                            setForm((f) => ({
                                ...f,
                                description: e.target.value,
                            }))
                        }
                        fullWidth
                        multiline
                        rows={3}
                    />
                    {/* Deck back art */}
                    <Box>
                        <Typography
                            sx={{ fontSize: "0.75rem", opacity: 0.5, mb: 0.5 }}
                        >
                            Deck Back Art
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <Box
                                onClick={() => backFileRef.current?.click()}
                                sx={{
                                    width: 80,
                                    height: 120,
                                    borderRadius: "8px",
                                    border: "2px dashed",
                                    borderColor: "divider",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "action.hover",
                                    "&:hover": { borderColor: "#8B0000" },
                                    backgroundImage: backImagePreview
                                        ? `url(${backImagePreview})`
                                        : "none",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            >
                                {!backImagePreview && (
                                    <>
                                        <CloudUpload
                                            sx={{ fontSize: 22, opacity: 0.4 }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "0.6rem",
                                                opacity: 0.5,
                                                mt: 0.5,
                                                textAlign: "center",
                                            }}
                                        >
                                            Back Art
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: "0.75rem",
                                        opacity: 0.6,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    This image appears on the back of every card
                                    in this deck. Stored at{" "}
                                    <code style={{ fontSize: "0.7rem" }}>
                                        backs/{form.deck || "deck-id"}.png
                                    </code>
                                </Typography>
                                {backImageFile && (
                                    <Chip
                                        label={backImageFile.name}
                                        size='small'
                                        sx={{ mt: 1, maxWidth: "100%" }}
                                        onDelete={() => {
                                            setBackImageFile(null)
                                            setBackImagePreview(
                                                isEdit
                                                    ? getDeckBackUrl(
                                                          initialDeck.deck,
                                                      )
                                                    : "",
                                            )
                                        }}
                                    />
                                )}
                            </Box>
                            <input
                                ref={backFileRef}
                                type='file'
                                accept='image/*'
                                style={{ display: "none" }}
                                onChange={handleBackImageChange}
                            />
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            mt: 1,
                        }}
                    >
                        <Button onClick={onClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            onClick={handleSave}
                            disabled={saving}
                            startIcon={
                                saving ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <Save />
                                )
                            }
                            sx={{
                                bgcolor: "#8B0000",
                                "&:hover": { bgcolor: "#a00" },
                                fontFamily: '"Cinzel", serif',
                                textTransform: "none",
                            }}
                        >
                            {saving ? "Saving…" : "Save Deck"}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Modal>
    )
}

// ═══════════════════════════════════════════════════════════
// Card Form Modal (Power + Monster)
// ═══════════════════════════════════════════════════════════
const POWER_DEFAULTS = {
    name: "",
    rarity: "common",
    description: "",
}
const MONSTER_DEFAULTS = {
    name: "",
    Description: "",
    Guise: "",
    Attack: "",
    Damage: "",
    "Hit Points Multiplier": "",
    Bloodied: "",
    Buffs: "",
    Crit: "",
    Immunities: "",
    "Special Weaknesses": "",
    Body: "",
    Agility: "",
    Focus: "",
    Fate: "",
    Insight: "",
}

const MotionPaper = motion(Paper)

function CardFormModal({
    open,
    onClose,
    onSave,
    initialCard,
    deck,
    navigateCard,
    cards = [],
    cardIndex = 0,
}) {
    const isEdit = !!initialCard
    const cardType = deck?.cardType || "power"
    const [form, setForm] = useState(
        cardType === "power" ? POWER_DEFAULTS : MONSTER_DEFAULTS,
    )
    const [imageFile, setImageFile] = useState(null)
    const [imagePreview, setImagePreview] = useState("")
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")
    const [isFlipping, setIsFlipping] = useState(false)
    const isFlippingRef = useRef(false)
    const controls = useAnimation()
    const fileRef = useRef()

    useEffect(() => {
        if (open) {
            setError("")
            setImageFile(null)
            setImagePreview("")
            if (isEdit) {
                setForm(
                    cardType === "power"
                        ? {
                              name: initialCard.name,
                              rarity: initialCard.rarity || "common",
                              description: initialCard.description || "",
                          }
                        : Object.fromEntries(
                              Object.keys(MONSTER_DEFAULTS).map((k) => [
                                  k,
                                  String(initialCard[k] ?? ""),
                              ]),
                          ),
                )
                if (initialCard.name && deck?.deck)
                    setImagePreview(getCardArtUrl(deck.deck, initialCard.name))
            } else {
                setForm(
                    cardType === "power" ? POWER_DEFAULTS : MONSTER_DEFAULTS,
                )
            }
        }
    }, [open, initialCard, isEdit, deck, cardType])

    // Navigate between cards in the modal using arrow keys
    const handleNavigate = useCallback(
        async (dir) => {
            if (isFlippingRef.current) return
            setIsFlipping(true)
            isFlippingRef.current = true

            const target = dir === "next" ? 90 : -90
            await controls.start({
                rotateY: target,
                transition: { duration: 0.22, ease: "easeInOut" },
            })

            navigateCard?.(dir)
            // Wait for the parent to update the card
            await new Promise((r) => setTimeout(r, 0))

            // reset to the opposite side before animating back to front
            await controls.set({ rotateY: -target })
            await controls.start({
                rotateY: 0,
                transition: { duration: 0.22, ease: "easeInOut" },
            })

            setIsFlipping(false)
            isFlippingRef.current = false
        },
        [controls, navigateCard],
    )

    // Navigate between cards in the modal using arrow keys
    useEffect(() => {
        if (!open || !navigateCard) return

        const onKeyDown = (e) => {
            if (e.key === "ArrowLeft") {
                e.preventDefault()
                handleNavigate("prev")
            } else if (e.key === "ArrowRight") {
                e.preventDefault()
                handleNavigate("next")
            }
        }

        window.addEventListener("keydown", onKeyDown)
        return () => window.removeEventListener("keydown", onKeyDown)
    }, [open, navigateCard, handleNavigate])

    const handleImageChange = (e) => {
        const file = e.target.files[0]
        if (!file) return
        setImageFile(file)
        setImagePreview(URL.createObjectURL(file))
    }

    const handleSave = async () => {
        if (!form.name?.trim()) {
            setError("Name is required.")
            return
        }
        setSaving(true)
        try {
            let artUrl = isEdit
                ? imageFile
                    ? ""
                    : getCardArtUrl(deck.deck, form.name)
                : ""
            // Upload image if one was selected
            if (imageFile) {
                const { uploadUrl, cdnUrl } = await adminFetch(
                    "/admin/upload-url",
                    "POST",
                    {
                        deck: deck.deck,
                        name: form.name,
                        folder: "cards",
                        contentType: imageFile.type || "image/png",
                    },
                )
                await fetch(uploadUrl, {
                    method: "PUT",
                    body: imageFile,
                    headers: { "Content-Type": imageFile.type || "image/png" },
                })
                artUrl = cdnUrl
            }
            const cardData = {
                ...form,
                deck: deck.deck,
                type: cardType,
                ...(artUrl ? { artUrl } : {}),
                ...(cardType === "monster" && form["Special Weaknesses"]
                    ? {
                          "Special Weaknesses": form["Special Weaknesses"]
                              .split(",")
                              .map((s) => s.trim())
                              .filter(Boolean),
                      }
                    : {}),
            }
            await onSave(cardData, isEdit)
            onClose()
        } catch (e) {
            setError(e.message)
        } finally {
            setSaving(false)
        }
    }

    const f = (key) => ({
        label: key,
        value: form[key] ?? "",
        onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })),
    })

    const prevCard = useMemo(() => {
        if (!cards || cards.length === 0) return null
        const idx = cardIndex % cards.length
        const prevIdx = (idx - 1 + cards.length) % cards.length
        return cards[prevIdx]
    }, [cards, cardIndex])

    const nextCard = useMemo(() => {
        if (!cards || cards.length === 0) return null
        const idx = cardIndex % cards.length
        const nextIdx = (idx + 1) % cards.length
        return cards[nextIdx]
    }, [cards, cardIndex])

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
            }}
        >
            <MotionPaper
                sx={{
                    width: { xs: "98%", sm: 640 },
                    maxHeight: "92vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    outline: "none",
                    perspective: 1200,
                    transformStyle: "preserve-3d",
                    backfaceVisibility: "hidden",
                    position: "relative",
                    overflow: "hidden",
                }}
            >
                {/* Left/Right nav buttons */}
                {navigateCard && (
                    <IconButton
                        onClick={() => handleNavigate("prev")}
                        disabled={isFlipping}
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: 8,
                            transform: "translateY(-50%)",
                            bgcolor: "rgba(0,0,0,0.4)",
                            color: "#fff",
                            zIndex: 2,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
                        }}
                    >
                        <ChevronLeft />
                    </IconButton>
                )}
                {navigateCard && (
                    <IconButton
                        onClick={() => handleNavigate("next")}
                        disabled={isFlipping}
                        sx={{
                            position: "absolute",
                            top: "50%",
                            right: 8,
                            transform: "translateY(-50%)",
                            bgcolor: "rgba(0,0,0,0.4)",
                            color: "#fff",
                            zIndex: 2,
                            "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                )}

                <motion.div
                    animate={controls}
                    initial={{ rotateY: 0 }}
                    style={{
                        width: "100%",
                        height: "100%",
                        position: "relative",
                        transformStyle: "preserve-3d",
                    }}
                >
                    {/* Side/back faces showing adjacent cards */}
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            transform: "rotateY(-90deg) translateZ(320px)",
                            backfaceVisibility: "hidden",
                            pointerEvents: "none",
                            overflow: "hidden",
                        }}
                    >
                        <img
                            src={getDeckBackUrl(deck?.deck)}
                            alt='Previous card back'
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                filter: "brightness(0.7)",
                            }}
                        />
                        {prevCard && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 16,
                                    left: 16,
                                    right: 16,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: "rgba(0,0,0,0.55)",
                                }}
                            >
                                <Typography
                                    variant='body2'
                                    sx={{ color: "#fff", fontWeight: "bold" }}
                                >
                                    Previous: {prevCard.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            transform: "rotateY(90deg) translateZ(320px)",
                            backfaceVisibility: "hidden",
                            pointerEvents: "none",
                            overflow: "hidden",
                        }}
                    >
                        <img
                            src={getDeckBackUrl(deck?.deck)}
                            alt='Next card back'
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                filter: "brightness(0.7)",
                            }}
                        />
                        {nextCard && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 16,
                                    left: 16,
                                    right: 16,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: "rgba(0,0,0,0.55)",
                                }}
                            >
                                <Typography
                                    variant='body2'
                                    sx={{ color: "#fff", fontWeight: "bold" }}
                                >
                                    Next: {nextCard.name}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Header */}
                    <Box
                        sx={{
                            p: 2.5,
                            bgcolor: "#1a0a0a",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"Cinzel", serif',
                                    fontWeight: "bold",
                                }}
                            >
                                {isEdit
                                    ? `Edit: ${initialCard?.name}`
                                    : `New ${cardType === "power" ? "Power" : "Monster"} Card`}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                            <Close />
                        </IconButton>
                    </Box>

                    {/* Body */}
                    <Box
                        sx={{
                            p: 3,
                            overflow: "auto",
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        {error && (
                            <Alert
                                severity='error'
                                onClose={() => setError("")}
                            >
                                {error}
                            </Alert>
                        )}

                        {/* Image Upload */}
                        <Box
                            sx={{
                                display: "flex",
                                gap: 2,
                                alignItems: "flex-start",
                            }}
                        >
                            <Box
                                onClick={() => fileRef.current?.click()}
                                sx={{
                                    width: 100,
                                    height: 150,
                                    borderRadius: "10px",
                                    border: "2px dashed",
                                    borderColor: "divider",
                                    cursor: "pointer",
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "action.hover",
                                    flexShrink: 0,
                                    "&:hover": {
                                        borderColor: "#8B0000",
                                        bgcolor: "rgba(139,0,0,0.04)",
                                    },
                                    backgroundImage: imagePreview
                                        ? `url(${imagePreview})`
                                        : "none",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                }}
                            >
                                {!imagePreview && (
                                    <>
                                        <CloudUpload
                                            sx={{ fontSize: 28, opacity: 0.4 }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: "0.65rem",
                                                opacity: 0.5,
                                                mt: 0.5,
                                            }}
                                        >
                                            Upload Art
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <input
                                    ref={fileRef}
                                    type='file'
                                    accept='image/*'
                                    style={{ display: "none" }}
                                    onChange={handleImageChange}
                                />
                                <TextField
                                    {...f("name")}
                                    label='Card Name'
                                    fullWidth
                                    required
                                    sx={{ mb: 1.5 }}
                                />
                                {cardType === "power" && (
                                    <FormControl fullWidth size='small'>
                                        <InputLabel>Rarity</InputLabel>
                                        <Select
                                            value={form.rarity || "common"}
                                            label='Rarity'
                                            onChange={(e) =>
                                                setForm((p) => ({
                                                    ...p,
                                                    rarity: e.target.value,
                                                }))
                                            }
                                        >
                                            {POWER_RARITIES.map((r) => (
                                                <MenuItem key={r} value={r}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 10,
                                                                height: 10,
                                                                borderRadius:
                                                                    "50%",
                                                                bgcolor:
                                                                    RARITY_COLORS[
                                                                        r
                                                                    ],
                                                            }}
                                                        />
                                                        {r
                                                            .charAt(0)
                                                            .toUpperCase() +
                                                            r.slice(1)}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </Box>
                        </Box>

                        {/* Power Card Fields */}
                        {cardType === "power" && (
                            <>
                                <TextField
                                    {...f("description")}
                                    label='Description'
                                    fullWidth
                                    multiline
                                    rows={3}
                                />
                            </>
                        )}

                        {/* Monster Card Fields */}
                        {cardType === "monster" && (
                            <>
                                <TextField
                                    {...f("Description")}
                                    label='Description / Flavour'
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                                <TextField
                                    {...f("Guise")}
                                    label='Guise (NPC appearance)'
                                    fullWidth
                                />
                                <Divider>
                                    <Typography
                                        sx={{
                                            fontSize: "0.7rem",
                                            opacity: 0.5,
                                            fontFamily: '"Cinzel", serif',
                                        }}
                                    >
                                        COMBAT
                                    </Typography>
                                </Divider>
                                <Grid container spacing={1.5}>
                                    <Grid item xs={6}>
                                        <TextField
                                            {...f("Attack")}
                                            label='Attack'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            {...f("Damage")}
                                            label='Damage'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            {...f("Hit Points Multiplier")}
                                            label='HP Multiplier'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            {...f("Bloodied")}
                                            label='Bloodied'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            {...f("Crit")}
                                            label='Crit Effect'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            {...f("Buffs")}
                                            label='Buffs / Special Abilities'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            {...f("Immunities")}
                                            label='Immunities'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            {...f("Special Weaknesses")}
                                            label='Weaknesses (comma-sep)'
                                            fullWidth
                                            size='small'
                                        />
                                    </Grid>
                                </Grid>
                                <Divider>
                                    <Typography
                                        sx={{
                                            fontSize: "0.7rem",
                                            opacity: 0.5,
                                            fontFamily: '"Cinzel", serif',
                                        }}
                                    >
                                        STATS
                                    </Typography>
                                </Divider>
                                <Grid container spacing={1.5}>
                                    {[
                                        "Body",
                                        "Agility",
                                        "Focus",
                                        "Fate",
                                        "Insight",
                                    ].map((stat) => (
                                        <Grid item xs={4} sm={2.4} key={stat}>
                                            <TextField
                                                {...f(stat)}
                                                label={stat}
                                                fullWidth
                                                size='small'
                                                type='number'
                                                inputProps={{ min: 0, max: 20 }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </>
                        )}
                    </Box>

                    {/* Footer */}
                    <Box
                        sx={{
                            p: 2,
                            borderTop: "1px solid",
                            borderColor: "divider",
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            flexShrink: 0,
                        }}
                    >
                        <Button onClick={onClose} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            onClick={handleSave}
                            disabled={saving}
                            startIcon={
                                saving ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <Save />
                                )
                            }
                            sx={{
                                bgcolor: "#8B0000",
                                "&:hover": { bgcolor: "#a00" },
                                fontFamily: '"Cinzel", serif',
                                textTransform: "none",
                            }}
                        >
                            {saving
                                ? "Saving…"
                                : `Save ${cardType === "power" ? "Power" : "Monster"}`}
                        </Button>
                    </Box>
                </motion.div>
            </MotionPaper>
        </Modal>
    )
}

// ═══════════════════════════════════════════════════════════
// Delete Confirm Modal
// ═══════════════════════════════════════════════════════════
function ConfirmDeleteModal({
    open,
    onClose,
    onConfirm,
    title,
    message,
    loading,
}) {
    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Paper
                sx={{
                    width: { xs: "calc(100vw - 32px)", sm: 400 },
                    maxWidth: 400,
                    borderRadius: "16px",
                    overflow: "hidden",
                    outline: "none",
                }}
            >
                <Box
                    sx={{
                        p: 2.5,
                        bgcolor: "#c62828",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Warning />
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Typography>{message}</Typography>
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                            mt: 3,
                        }}
                    >
                        <Button onClick={onClose} disabled={loading}>
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            color='error'
                            onClick={onConfirm}
                            disabled={loading}
                            startIcon={
                                loading ? (
                                    <CircularProgress size={14} />
                                ) : (
                                    <Delete />
                                )
                            }
                        >
                            {loading ? "Deleting…" : "Delete"}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Modal>
    )
}

// ═══════════════════════════════════════════════════════════
// CSV Import Modal
// ═══════════════════════════════════════════════════════════
function CsvImportModal({ open, onClose, onImport, deck }) {
    const cardType = deck?.cardType || "power"
    const expectedCols =
        cardType === "monster" ? MONSTER_CSV_COLS : POWER_CSV_COLS
    const [parsedRows, setParsedRows] = useState([])
    const [parseError, setParseError] = useState("")
    const [importing, setImporting] = useState(false)
    const [importResult, setImportResult] = useState(null)
    const [fileName, setFileName] = useState("")
    const [pasteCsv, setPasteCsv] = useState("")
    const fileRef = useRef()

    useEffect(() => {
        if (open) {
            setParsedRows([])
            setParseError("")
            setImporting(false)
            setImportResult(null)
            setFileName("")
        }
    }, [open])

    const handleFile = (e) => {
        const file = e.target.files[0]
        if (!file) return
        setFileName(file.name)
        setParseError("")
        setParsedRows([])
        setImportResult(null)
        const reader = new FileReader()
        reader.onload = (ev) => {
            parseCsvText(ev.target.result)
        }
        reader.readAsText(file)
    }

    const parseCsvText = (text) => {
        setParseError("")
        setParsedRows([])
        setImportResult(null)
        try {
            const { headers, rows } = parseCsv(text)
            // Warn about unrecognised columns but don't block
            const unknown = headers.filter(
                (h) =>
                    !expectedCols.includes(h) && h !== "deck" && h !== "type",
            )
            if (!headers.includes("name"))
                throw new Error('CSV must have a "name" column.')
            if (unknown.length)
                setParseError(
                    `Note: unknown columns will be ignored: ${unknown.join(", ")}`,
                )
            setParsedRows(rows)
        } catch (err) {
            setParseError(err.message)
        }
    }

    const handleImport = async () => {
        setImporting(true)
        setImportResult(null)
        try {
            const cardsToImport = parsedRows
                .filter((r) => r.name?.trim())
                .map((r) => ({
                    ...r,
                    deck: deck.deck,
                    type: cardType,
                    rarity: r.rarity || "common",
                }))
            const result = await onImport(cardsToImport)
            setImportResult(result)
            setParsedRows([])
            setFileName("")
            setPasteCsv("")
        } catch (err) {
            setParseError(err.message)
        } finally {
            setImporting(false)
        }
    }

    const previewCols = expectedCols.slice(0, 4)
    const validRows = parsedRows.filter((r) => r.name?.trim())

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
            }}
        >
            <Paper
                sx={{
                    width: { xs: "98%", sm: 700 },
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    outline: "none",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2.5,
                        bgcolor: "#1a0a0a",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                        <FileUpload sx={{ color: "#8B0000" }} />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: '"Cinzel", serif',
                                    fontWeight: "bold",
                                }}
                            >
                                Import Cards from CSV
                            </Typography>
                            <Typography
                                sx={{ fontSize: "0.7rem", opacity: 0.5 }}
                            >
                                Deck: {deck?.displayName}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <Close />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        p: 3,
                        overflow: "auto",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {/* Format hint */}
                    <Paper
                        variant='outlined'
                        sx={{
                            p: 1.5,
                            bgcolor: "rgba(139,0,0,0.04)",
                            borderColor: "rgba(139,0,0,0.2)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: "bold",
                                mb: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <TableChart sx={{ fontSize: 14 }} /> Expected
                            columns ({cardType})
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.68rem",
                                fontFamily: "monospace",
                                opacity: 0.7,
                                wordBreak: "break-all",
                            }}
                        >
                            {expectedCols.join(", ")}
                        </Typography>
                    </Paper>

                    {/* File picker */}
                    <Box
                        onClick={() => fileRef.current?.click()}
                        sx={{
                            border: "2px dashed",
                            borderColor: parsedRows.length
                                ? "#2e7d32"
                                : "divider",
                            borderRadius: "12px",
                            p: 3,
                            textAlign: "center",
                            cursor: "pointer",
                            bgcolor: parsedRows.length
                                ? "rgba(46,125,50,0.04)"
                                : "action.hover",
                            "&:hover": { borderColor: "#8B0000" },
                            transition: "border-color 0.2s",
                        }}
                    >
                        <input
                            ref={fileRef}
                            type='file'
                            accept='.csv,text/csv'
                            style={{ display: "none" }}
                            onChange={handleFile}
                        />
                        {parsedRows.length ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                <CheckCircle sx={{ color: "#2e7d32" }} />
                                <Typography sx={{ fontWeight: "bold" }}>
                                    {fileName}
                                </Typography>
                                <Chip
                                    label={`${validRows.length} card${validRows.length !== 1 ? "s" : ""}`}
                                    size='small'
                                    color='success'
                                />
                            </Box>
                        ) : (
                            <>
                                <FileUpload
                                    sx={{ fontSize: 36, opacity: 0.3, mb: 1 }}
                                />
                                <Typography sx={{ opacity: 0.5 }}>
                                    Click to choose a .csv file
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Paste CSV text */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "0.75rem",
                                opacity: 0.6,
                                mb: 1,
                            }}
                        >
                            Or paste CSV text below and click “Parse”.
                        </Typography>
                        <TextField
                            multiline
                            minRows={4}
                            maxRows={10}
                            fullWidth
                            placeholder='name,rarity,description\nFireball,Rare,Launch a fireball...'
                            value={pasteCsv}
                            onChange={(e) => setPasteCsv(e.target.value)}
                        />
                        <Button
                            size='small'
                            variant='contained'
                            color='primary'
                            sx={{ mt: 1 }}
                            onClick={() => parseCsvText(pasteCsv)}
                        >
                            Parse pasted CSV
                        </Button>
                    </Box>

                    {parseError && (
                        <Alert
                            severity={parsedRows.length ? "warning" : "error"}
                            onClose={() => setParseError("")}
                        >
                            {parseError}
                        </Alert>
                    )}

                    {/* Preview table */}
                    {parsedRows.length > 0 && !importResult && (
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    opacity: 0.5,
                                    mb: 1,
                                }}
                            >
                                Preview (first 5 rows)
                            </Typography>
                            <Box
                                sx={{
                                    overflow: "auto",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "8px",
                                }}
                            >
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                bgcolor: "rgba(139,0,0,0.06)",
                                            }}
                                        >
                                            {previewCols.map((col) => (
                                                <TableCell
                                                    key={col}
                                                    sx={{
                                                        fontFamily:
                                                            '"Cinzel", serif',
                                                        fontSize: "0.65rem",
                                                        fontWeight: "bold",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {col}
                                                </TableCell>
                                            ))}
                                            {parsedRows[0] &&
                                                Object.keys(parsedRows[0])
                                                    .length >
                                                    previewCols.length && (
                                                    <TableCell
                                                        sx={{
                                                            fontSize: "0.65rem",
                                                            opacity: 0.4,
                                                        }}
                                                    >
                                                        +
                                                        {Object.keys(
                                                            parsedRows[0],
                                                        ).length -
                                                            previewCols.length}{" "}
                                                        more
                                                    </TableCell>
                                                )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsedRows
                                            .slice(0, 5)
                                            .map((row, i) => (
                                                <TableRow key={i}>
                                                    {previewCols.map((col) => (
                                                        <TableCell
                                                            key={col}
                                                            sx={{
                                                                fontSize:
                                                                    "0.7rem",
                                                                maxWidth: 160,
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {row[col] || "—"}
                                                        </TableCell>
                                                    ))}
                                                    {Object.keys(parsedRows[0])
                                                        .length >
                                                        previewCols.length && (
                                                        <TableCell />
                                                    )}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    )}

                    {importing && <LinearProgress sx={{ borderRadius: 4 }} />}

                    {importResult && (
                        <Alert severity='success' icon={<CheckCircle />}>
                            Imported {importResult.imported} card
                            {importResult.imported !== 1 ? "s" : ""}{" "}
                            successfully
                            {importResult.failed > 0
                                ? ` (${importResult.failed} failed)`
                                : "."}{" "}
                            Click Close to view them.
                        </Alert>
                    )}
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1,
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                    }}
                >
                    <Typography sx={{ fontSize: "0.72rem", opacity: 0.4 }}>
                        Existing cards with the same name will be overwritten.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button onClick={onClose}>
                            {importResult ? "Close" : "Cancel"}
                        </Button>
                        {!importResult && (
                            <Button
                                variant='contained'
                                onClick={handleImport}
                                disabled={validRows.length === 0 || importing}
                                startIcon={
                                    importing ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <FileUpload />
                                    )
                                }
                                sx={{
                                    bgcolor: "#8B0000",
                                    "&:hover": { bgcolor: "#a00" },
                                    fontFamily: '"Cinzel", serif',
                                    textTransform: "none",
                                }}
                            >
                                {importing
                                    ? "Importing…"
                                    : `Import ${validRows.length} Card${validRows.length !== 1 ? "s" : ""}`}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Modal>
    )
}

// ═══════════════════════════════════════════════════════════
// Equipment CSV Import Modal
// ═══════════════════════════════════════════════════════════
function EquipmentCsvImportModal({ open, onClose, onImport }) {
    const [parsedRows, setParsedRows] = useState([])
    const [parseError, setParseError] = useState("")
    const [importing, setImporting] = useState(false)
    const [importResult, setImportResult] = useState(null)
    const [fileName, setFileName] = useState("")
    const [pasteCsv, setPasteCsv] = useState("")
    const fileRef = useRef()

    useEffect(() => {
        if (open) {
            setParsedRows([])
            setParseError("")
            setImporting(false)
            setImportResult(null)
            setFileName("")
            setPasteCsv("")
        }
    }, [open])

    const parseCsvText = (text) => {
        setParseError("")
        setParsedRows([])
        setImportResult(null)
        try {
            const { headers, rows } = parseCsv(text)
            const unknown = headers.filter(
                (h) => !EQUIPMENT_CSV_COLS.includes(h),
            )
            if (!headers.includes("name"))
                throw new Error('CSV must have a "name" column.')
            if (!headers.includes("subcategory"))
                throw new Error('CSV must have a "subcategory" column.')
            if (unknown.length)
                setParseError(
                    `Note: unknown columns will be ignored: ${unknown.join(", ")}`,
                )
            setParsedRows(rows)
        } catch (err) {
            setParseError(err.message)
        }
    }

    const handleFile = (e) => {
        const file = e.target.files[0]
        if (!file) return
        setFileName(file.name)
        setParseError("")
        setParsedRows([])
        setImportResult(null)
        const reader = new FileReader()
        reader.onload = (ev) => parseCsvText(ev.target.result)
        reader.readAsText(file)
    }

    const handleImport = async () => {
        setImporting(true)
        setImportResult(null)
        try {
            const itemsToImport = parsedRows
                .filter((r) => r.name?.trim() && r.subcategory?.trim())
                .map((r) => ({
                    name: r.name.trim(),
                    subcategory: r.subcategory.trim(),
                    tier: r.tier?.trim() || "Low Pay",
                    description: r.description?.trim() || "",
                    ...(r.damageType?.trim() && {
                        damageType: r.damageType.trim(),
                    }),
                    ...(r.range?.trim() && { range: r.range.trim() }),
                    ...(r.protects?.trim() && { protects: r.protects.trim() }),
                    ...(r.uses?.trim() && { uses: parseInt(r.uses, 10) }),
                    ...(r.trick?.trim() && { trick: r.trick.trim() }),
                }))
            const result = await onImport(itemsToImport)
            setImportResult(result)
            setParsedRows([])
            setFileName("")
            setPasteCsv("")
        } catch (err) {
            setParseError(err.message)
        } finally {
            setImporting(false)
        }
    }

    const validRows = parsedRows.filter(
        (r) => r.name?.trim() && r.subcategory?.trim(),
    )
    const previewCols = ["name", "subcategory", "tier", "description"]

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
            }}
        >
            <Paper
                sx={{
                    width: { xs: "98%", sm: 700 },
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    outline: "none",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2.5,
                        bgcolor: "#1a0a0a",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                        <FileUpload sx={{ color: "#8B0000" }} />
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: '"Cinzel", serif',
                                    fontWeight: "bold",
                                }}
                            >
                                Import Equipment from CSV
                            </Typography>
                            <Typography
                                sx={{ fontSize: "0.7rem", opacity: 0.5 }}
                            >
                                Hunter\'s Arsenal
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <Close />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        p: 3,
                        overflow: "auto",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {/* Format hint */}
                    <Paper
                        variant='outlined'
                        sx={{
                            p: 1.5,
                            bgcolor: "rgba(139,0,0,0.04)",
                            borderColor: "rgba(139,0,0,0.2)",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: "bold",
                                mb: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <TableChart sx={{ fontSize: 14 }} /> Expected
                            columns
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.68rem",
                                fontFamily: "monospace",
                                opacity: 0.7,
                                wordBreak: "break-all",
                            }}
                        >
                            {EQUIPMENT_CSV_COLS.join(", ")}
                        </Typography>
                    </Paper>

                    {/* File picker */}
                    <Box
                        onClick={() => fileRef.current?.click()}
                        sx={{
                            border: "2px dashed",
                            borderColor: parsedRows.length
                                ? "#2e7d32"
                                : "divider",
                            borderRadius: "12px",
                            p: 3,
                            textAlign: "center",
                            cursor: "pointer",
                            bgcolor: parsedRows.length
                                ? "rgba(46,125,50,0.04)"
                                : "action.hover",
                            "&:hover": { borderColor: "#8B0000" },
                            transition: "border-color 0.2s",
                        }}
                    >
                        <input
                            ref={fileRef}
                            type='file'
                            accept='.csv,text/csv'
                            style={{ display: "none" }}
                            onChange={handleFile}
                        />
                        {parsedRows.length ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                }}
                            >
                                <CheckCircle sx={{ color: "#2e7d32" }} />
                                <Typography sx={{ fontWeight: "bold" }}>
                                    {fileName}
                                </Typography>
                                <Chip
                                    label={`${validRows.length} item${validRows.length !== 1 ? "s" : ""}`}
                                    size='small'
                                    color='success'
                                />
                            </Box>
                        ) : (
                            <>
                                <FileUpload
                                    sx={{ fontSize: 36, opacity: 0.3, mb: 1 }}
                                />
                                <Typography sx={{ opacity: 0.5 }}>
                                    Click to choose a .csv file
                                </Typography>
                            </>
                        )}
                    </Box>

                    {/* Paste CSV text */}
                    <Box>
                        <Typography
                            sx={{ fontSize: "0.75rem", opacity: 0.6, mb: 1 }}
                        >
                            Or paste CSV text below and click "Parse".
                        </Typography>
                        <TextField
                            multiline
                            minRows={4}
                            maxRows={10}
                            fullWidth
                            placeholder='name,subcategory,tier,description\nSword of Ruin,Weapon,Low Pay,A devastating blade...'
                            value={pasteCsv}
                            onChange={(e) => setPasteCsv(e.target.value)}
                        />
                        <Button
                            size='small'
                            variant='contained'
                            color='primary'
                            sx={{ mt: 1 }}
                            onClick={() => parseCsvText(pasteCsv)}
                        >
                            Parse pasted CSV
                        </Button>
                    </Box>

                    {parseError && (
                        <Alert
                            severity={parsedRows.length ? "warning" : "error"}
                            onClose={() => setParseError("")}
                        >
                            {parseError}
                        </Alert>
                    )}

                    {/* Preview table */}
                    {parsedRows.length > 0 && !importResult && (
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: "0.75rem",
                                    opacity: 0.5,
                                    mb: 1,
                                }}
                            >
                                Preview (first 5 rows)
                            </Typography>
                            <Box
                                sx={{
                                    overflow: "auto",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: "8px",
                                }}
                            >
                                <Table size='small'>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                bgcolor: "rgba(139,0,0,0.06)",
                                            }}
                                        >
                                            {previewCols.map((col) => (
                                                <TableCell
                                                    key={col}
                                                    sx={{
                                                        fontFamily:
                                                            '"Cinzel", serif',
                                                        fontSize: "0.65rem",
                                                        fontWeight: "bold",
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    {col}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {parsedRows
                                            .slice(0, 5)
                                            .map((row, i) => (
                                                <TableRow key={i}>
                                                    {previewCols.map((col) => (
                                                        <TableCell
                                                            key={col}
                                                            sx={{
                                                                fontSize:
                                                                    "0.7rem",
                                                                maxWidth: 160,
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis",
                                                                whiteSpace:
                                                                    "nowrap",
                                                            }}
                                                        >
                                                            {row[col] || "—"}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    )}

                    {importing && <LinearProgress sx={{ borderRadius: 4 }} />}

                    {importResult && (
                        <Alert severity='success' icon={<CheckCircle />}>
                            Imported {importResult.imported} item
                            {importResult.imported !== 1 ? "s" : ""}{" "}
                            successfully
                            {importResult.failed > 0
                                ? ` (${importResult.failed} failed)`
                                : "."}{" "}
                            Click Close to view them.
                        </Alert>
                    )}
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1,
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexShrink: 0,
                    }}
                >
                    <Typography sx={{ fontSize: "0.72rem", opacity: 0.4 }}>
                        Existing items with the same name + subcategory will be
                        overwritten.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button onClick={onClose}>
                            {importResult ? "Close" : "Cancel"}
                        </Button>
                        {!importResult && (
                            <Button
                                variant='contained'
                                onClick={handleImport}
                                disabled={validRows.length === 0 || importing}
                                startIcon={
                                    importing ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <FileUpload />
                                    )
                                }
                                sx={{
                                    bgcolor: "#8B0000",
                                    "&:hover": { bgcolor: "#a00" },
                                    fontFamily: '"Cinzel", serif',
                                    textTransform: "none",
                                }}
                            >
                                {importing
                                    ? "Importing…"
                                    : `Import ${validRows.length} Item${validRows.length !== 1 ? "s" : ""}`}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Modal>
    )
}

// ═══════════════════════════════════════════════════════════
// Item Form Modal (Equipment CRUD)
// ═══════════════════════════════════════════════════════════
function ItemFormModal({ open, onClose, onSave, initialItem }) {
    const isEdit = !!initialItem
    const [form, setForm] = useState(ITEM_DEFAULTS)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    useEffect(() => {
        if (open) {
            setError("")
            if (isEdit) {
                setForm({
                    name: initialItem.name || "",
                    subcategory: initialItem.subcategory || "Weapon",
                    tier: initialItem.tier || "Low Pay",
                    description: initialItem.description || "",
                    damageType: initialItem.damageType || "",
                    range: initialItem.range || "",
                    protects: initialItem.protects || "",
                    uses:
                        initialItem.uses != null
                            ? String(initialItem.uses)
                            : "",
                    trick: initialItem.trick || "",
                })
            } else {
                setForm(ITEM_DEFAULTS)
            }
        }
    }, [open, initialItem, isEdit])

    const handleSave = async () => {
        if (!form.name.trim()) {
            setError("Name is required.")
            return
        }
        if (!form.description.trim()) {
            setError("Description is required.")
            return
        }
        setSaving(true)
        try {
            const itemData = { ...form }
            if (form.uses !== "") itemData.uses = parseInt(form.uses, 10)
            else
                delete itemData.uses
                // Remove blank optional fields
            ;["damageType", "range", "protects", "trick"].forEach((k) => {
                if (!itemData[k]) delete itemData[k]
            })
            await onSave(itemData, isEdit ? initialItem : null)
            onClose()
        } catch (e) {
            setError(e.message)
        } finally {
            setSaving(false)
        }
    }

    const f = (key) => ({
        value: form[key] ?? "",
        onChange: (e) => setForm((p) => ({ ...p, [key]: e.target.value })),
    })

    const accentColor = SUBCATEGORY_COLORS[form.subcategory] || "#8B0000"

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
            }}
        >
            <Paper
                sx={{
                    width: { xs: "98%", sm: 560 },
                    maxHeight: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: "16px",
                    outline: "none",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 2.5,
                        bgcolor: accentColor,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                        }}
                    >
                        {isEdit ? `Edit: ${initialItem?.name}` : "New Item"}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <Close />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box
                    sx={{
                        p: 3,
                        overflow: "auto",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {error && (
                        <Alert severity='error' onClose={() => setError("")}>
                            {error}
                        </Alert>
                    )}

                    <TextField
                        label='Name'
                        {...f("name")}
                        fullWidth
                        required
                        autoFocus
                    />

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Subcategory</InputLabel>
                                <Select
                                    value={form.subcategory}
                                    label='Subcategory'
                                    onChange={(e) =>
                                        setForm((p) => ({
                                            ...p,
                                            subcategory: e.target.value,
                                        }))
                                    }
                                >
                                    {SUBCATEGORY_ORDER.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: "50%",
                                                        bgcolor:
                                                            SUBCATEGORY_COLORS[
                                                                s
                                                            ],
                                                    }}
                                                />
                                                {s}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Tier</InputLabel>
                                <Select
                                    value={form.tier}
                                    label='Tier'
                                    onChange={(e) =>
                                        setForm((p) => ({
                                            ...p,
                                            tier: e.target.value,
                                        }))
                                    }
                                >
                                    {TIER_OPTIONS.map((t) => (
                                        <MenuItem key={t} value={t}>
                                            {t}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <TextField
                        label='Description'
                        {...f("description")}
                        fullWidth
                        multiline
                        rows={3}
                        required
                    />

                    {form.subcategory === "Weapon" && (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label='Damage Type'
                                    {...f("damageType")}
                                    fullWidth
                                    size='small'
                                    placeholder='Physical / Spiritual / Hybrid'
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label='Range'
                                    {...f("range")}
                                    fullWidth
                                    size='small'
                                    placeholder='Close / Near / Far'
                                />
                            </Grid>
                        </Grid>
                    )}
                    {form.subcategory === "Protection" && (
                        <TextField
                            label='Protects Against'
                            {...f("protects")}
                            fullWidth
                            size='small'
                            placeholder='Physical, Spiritual, Hybrid'
                        />
                    )}
                    {(form.subcategory === "Control" ||
                        form.subcategory === "Denial") && (
                        <TextField
                            label='Uses (per hunt)'
                            {...f("uses")}
                            fullWidth
                            size='small'
                            type='number'
                            inputProps={{ min: 1 }}
                        />
                    )}

                    <TextField
                        label='Trick / Special Effect (optional)'
                        {...f("trick")}
                        fullWidth
                        multiline
                        rows={2}
                    />
                </Box>

                {/* Footer */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                    }}
                >
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        onClick={handleSave}
                        disabled={saving}
                        startIcon={
                            saving ? <CircularProgress size={14} /> : <Save />
                        }
                        sx={{
                            bgcolor: accentColor,
                            "&:hover": { filter: "brightness(1.15)" },
                            fontFamily: '"Cinzel", serif',
                            textTransform: "none",
                        }}
                    >
                        {saving
                            ? "Saving…"
                            : isEdit
                              ? "Save Changes"
                              : "Create Item"}
                    </Button>
                </Box>
            </Paper>
        </Modal>
    )
}

// ═══════════════════════════════════════════════════════════
// Admin Rules Panel
// ═══════════════════════════════════════════════════════════
function AdminRulesPanel() {
    const [grouped, setGrouped] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [expandedCat, setExpandedCat] = useState(null)
    const [search, setSearch] = useState("")
    const [editModal, setEditModal] = useState(null) // { category, sectionId, section }
    const [editTitle, setEditTitle] = useState("")
    const [editJson, setEditJson] = useState("")
    const [jsonError, setJsonError] = useState("")
    const [saving, setSaving] = useState(false)
    const [saveMsg, setSaveMsg] = useState("")
    const [deleting, setDeleting] = useState(null)
    const [seeding, setSeeding] = useState(false)
    const [seedMsg, setSeedMsg] = useState("")
    const [exportOpen, setExportOpen] = useState(false)
    const [exportVersion, setExportVersion] = useState(CURRENT_VERSION)
    const [exporting, setExporting] = useState(false)
    const [exportError, setExportError] = useState("")

    // Load live version from DB when panel mounts
    useEffect(() => {
        getWhitepaperVersion().then((v) => setExportVersion(v))
    }, [])

    const loadRules = useCallback(async () => {
        setLoading(true)
        setError("")
        try {
            const res = await fetch(`${API_URL}/rules`)
            const data = await res.json()
            setGrouped(data.grouped || {})
        } catch (e) {
            setError("Failed to load rules: " + e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadRules()
    }, [loadRules])

    // ── Edit section ──────────────────────────────────────────────────────────
    const openEdit = (category, section) => {
        setEditModal({ category, sectionId: section.sectionId, section })
        setEditTitle(section.content?.title || section.title || "")
        setEditJson(JSON.stringify(section.content || {}, null, 2))
        setJsonError("")
        setSaveMsg("")
    }

    const handleSave = async () => {
        let parsed
        try {
            parsed = JSON.parse(editJson)
        } catch (e) {
            setJsonError("Invalid JSON: " + e.message)
            return
        }
        parsed.title = editTitle || parsed.title || ""
        parsed.id = editModal.sectionId
        setSaving(true)
        setSaveMsg("")
        try {
            await adminFetch(
                `/rules/${encodeURIComponent(editModal.category)}/${encodeURIComponent(editModal.sectionId)}`,
                "PUT",
                {
                    title: parsed.title,
                    order: editModal.section.order ?? 0,
                    content: parsed,
                    updatedBy:
                        localStorage.getItem("imk_admin_email") || "admin",
                },
            )
            setSaveMsg("Saved!")
            // Bump whitepaper version on every successful rule edit
            const idToken = localStorage.getItem("imk_admin_id_token")
            bumpWhitepaperVersion(idToken, exportVersion).then((newVer) =>
                setExportVersion(newVer),
            )
            await loadRules()
            setTimeout(() => {
                setEditModal(null)
                setSaveMsg("")
            }, 1200)
        } catch (e) {
            setSaveMsg("Error: " + e.message)
        } finally {
            setSaving(false)
        }
    }

    // ── Delete section ────────────────────────────────────────────────────────
    const handleDelete = async (category, sectionId) => {
        setDeleting(`${category}/${sectionId}`)
        try {
            await adminFetch(
                `/rules/${encodeURIComponent(category)}/${encodeURIComponent(sectionId)}`,
                "DELETE",
            )
            await loadRules()
        } catch (e) {
            setError("Delete failed: " + e.message)
        } finally {
            setDeleting(null)
        }
    }

    // ── Re-seed from public JSON files ────────────────────────────────────────
    const handleReseed = async () => {
        setSeeding(true)
        setSeedMsg("")
        try {
            // Load rules-database.json
            const dbRes = await fetch("/rules-database.json")
            const db = await dbRes.json()
            const categories = db.rulesDatabase.categories

            // Fetch each category file and extract sections
            const sections = []
            for (const [catKey, meta] of Object.entries(categories)) {
                const catRes = await fetch(`/${meta.file}`)
                const catRaw = await catRes.json()
                const catData = Object.values(catRaw)[0]
                if (catData?.sections) {
                    catData.sections.forEach((sec, idx) => {
                        sections.push({
                            category: catKey,
                            sectionId: sec.id,
                            title: sec.title,
                            order: idx,
                            content: sec,
                        })
                    })
                }
            }

            await adminFetch("/rules/seed", "POST", {
                sections,
                updatedBy: localStorage.getItem("imk_admin_email") || "admin",
            })
            setSeedMsg(`✅ Seeded ${sections.length} sections from JSON files`)
            await loadRules()
        } catch (e) {
            setSeedMsg("❌ Seed failed: " + e.message)
        } finally {
            setSeeding(false)
        }
    }

    // ── Export Whitepapers ────────────────────────────────────────────────────
    const handleExport = async () => {
        setExporting(true)
        setExportError("")
        try {
            // Fetch live equipment items + static system metadata in parallel
            const [eqApi, eqJson] = await Promise.all([
                adminFetch("/equipment"),
                fetch("/equipment.json").then((r) => r.json()),
            ])
            const equipmentData = {
                system: eqJson?.equipmentSystem || null,
                items: eqApi?.equipment || [],
            }
            await exportWhitepapers(grouped, exportVersion, null, equipmentData)
            setExportOpen(false)
        } catch (e) {
            setExportError("Export failed: " + e.message)
        } finally {
            setExporting(false)
        }
    }

    // ── Filter ────────────────────────────────────────────────────────────────
    const lowerSearch = search.toLowerCase()
    const filteredGrouped = {}
    Object.entries(grouped).forEach(([cat, sections]) => {
        const matches = sections.filter(
            (s) =>
                !lowerSearch ||
                (s.content?.title || s.title || "")
                    .toLowerCase()
                    .includes(lowerSearch) ||
                s.sectionId.toLowerCase().includes(lowerSearch) ||
                cat.toLowerCase().includes(lowerSearch),
        )
        if (matches.length > 0) filteredGrouped[cat] = matches
    })

    const totalSections = Object.values(grouped).reduce(
        (n, arr) => n + arr.length,
        0,
    )

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Header row */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                        }}
                    >
                        Rules Database
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", opacity: 0.5 }}>
                        {totalSections} section{totalSections !== 1 ? "s" : ""}{" "}
                        across {Object.keys(grouped).length} categories
                        {totalSections === 0
                            ? " — seed from JSON files to populate"
                            : ""}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        size='small'
                        variant='outlined'
                        onClick={loadRules}
                        disabled={loading}
                        sx={{ fontSize: "0.72rem", textTransform: "none" }}
                    >
                        Refresh
                    </Button>
                    <Button
                        size='small'
                        onClick={handleReseed}
                        disabled={seeding}
                        sx={{
                            bgcolor: "#8B0000",
                            color: "#fff",
                            fontSize: "0.72rem",
                            textTransform: "none",
                            "&:hover": { bgcolor: "#a00" },
                        }}
                    >
                        {seeding ? "Seeding…" : "Re-seed from JSON"}
                    </Button>
                    <Button
                        size='small'
                        variant='outlined'
                        onClick={() => {
                            setExportVersion(CURRENT_VERSION)
                            setExportError("")
                            setExportOpen(true)
                        }}
                        disabled={totalSections === 0}
                        startIcon={<FileDownload sx={{ fontSize: 15 }} />}
                        sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            borderColor: "rgba(139,0,0,0.4)",
                            color: "#8B0000",
                            "&:hover": {
                                borderColor: "#8B0000",
                                bgcolor: "rgba(139,0,0,0.04)",
                            },
                        }}
                    >
                        Export Whitepapers
                    </Button>
                </Box>
            </Box>

            {seedMsg && (
                <Alert
                    severity={seedMsg.startsWith("✅") ? "success" : "error"}
                    onClose={() => setSeedMsg("")}
                    sx={{ mb: 2 }}
                >
                    {seedMsg}
                </Alert>
            )}
            {error && (
                <Alert
                    severity='error'
                    onClose={() => setError("")}
                    sx={{ mb: 2 }}
                >
                    {error}
                </Alert>
            )}

            {/* Search */}
            <TextField
                placeholder='Search sections…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size='small'
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                    startAdornment: (
                        <Typography sx={{ mr: 1, opacity: 0.4 }}>🔍</Typography>
                    ),
                }}
            />

            {loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                Object.entries(filteredGrouped)
                    .sort()
                    .map(([cat, sections]) => (
                        <Paper
                            key={cat}
                            sx={{
                                mb: 1.5,
                                borderRadius: "12px",
                                overflow: "hidden",
                                border: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            {/* Category header */}
                            <Box
                                onClick={() =>
                                    setExpandedCat(
                                        expandedCat === cat ? null : cat,
                                    )
                                }
                                sx={{
                                    px: 2,
                                    py: 1.2,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    cursor: "pointer",
                                    bgcolor: (theme) =>
                                        theme.palette.mode === "dark"
                                            ? "rgba(255,255,255,0.05)"
                                            : "rgba(0,0,0,0.04)",
                                    "&:hover": {
                                        bgcolor: (theme) =>
                                            theme.palette.mode === "dark"
                                                ? "rgba(255,255,255,0.08)"
                                                : "rgba(0,0,0,0.07)",
                                    },
                                }}
                            >
                                <Box>
                                    <Typography
                                        sx={{
                                            fontFamily: '"Cinzel", serif',
                                            fontWeight: "bold",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {cat}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "0.65rem",
                                            opacity: 0.5,
                                        }}
                                    >
                                        {sections.length} section
                                        {sections.length !== 1 ? "s" : ""}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{ opacity: 0.4, fontSize: "0.8rem" }}
                                >
                                    {expandedCat === cat ? "▲" : "▼"}
                                </Typography>
                            </Box>

                            {/* Sections list */}
                            {expandedCat === cat && (
                                <Box>
                                    <Divider />
                                    {sections
                                        .sort(
                                            (a, b) =>
                                                (a.order ?? 0) - (b.order ?? 0),
                                        )
                                        .map((sec) => {
                                            const key = `${cat}/${sec.sectionId}`
                                            return (
                                                <Box
                                                    key={sec.sectionId}
                                                    sx={{
                                                        px: 2,
                                                        py: 1,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent:
                                                            "space-between",
                                                        borderBottom:
                                                            "1px solid",
                                                        borderColor: "divider",
                                                        "&:last-child": {
                                                            borderBottom:
                                                                "none",
                                                        },
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontWeight:
                                                                    "bold",
                                                                fontSize:
                                                                    "0.8rem",
                                                            }}
                                                        >
                                                            {sec.content
                                                                ?.title ||
                                                                sec.title ||
                                                                sec.sectionId}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize:
                                                                    "0.65rem",
                                                                opacity: 0.45,
                                                            }}
                                                        >
                                                            id: {sec.sectionId}
                                                            {sec.lastUpdated
                                                                ? `  ·  updated ${new Date(sec.lastUpdated).toLocaleDateString()}`
                                                                : ""}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <Tooltip title='Edit section'>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() =>
                                                                    openEdit(
                                                                        cat,
                                                                        sec,
                                                                    )
                                                                }
                                                                sx={{
                                                                    color: "#ffa000",
                                                                    opacity: 0.8,
                                                                    "&:hover": {
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            >
                                                                <Edit
                                                                    sx={{
                                                                        fontSize: 15,
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title='Delete section'>
                                                            <IconButton
                                                                size='small'
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        cat,
                                                                        sec.sectionId,
                                                                    )
                                                                }
                                                                disabled={
                                                                    deleting ===
                                                                    key
                                                                }
                                                                sx={{
                                                                    color: "#e57373",
                                                                    opacity: 0.8,
                                                                    "&:hover": {
                                                                        opacity: 1,
                                                                    },
                                                                }}
                                                            >
                                                                {deleting ===
                                                                key ? (
                                                                    <CircularProgress
                                                                        size={
                                                                            12
                                                                        }
                                                                        color='inherit'
                                                                    />
                                                                ) : (
                                                                    <Delete
                                                                        sx={{
                                                                            fontSize: 15,
                                                                        }}
                                                                    />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Box>
                                            )
                                        })}
                                </Box>
                            )}
                        </Paper>
                    ))
            )}

            {/* Edit Modal */}
            {/* Export Whitepapers Modal */}
            <Modal
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Paper
                    sx={{
                        width: { xs: "calc(100vw - 32px)", sm: 440 },
                        maxWidth: 440,
                        borderRadius: "16px",
                        overflow: "hidden",
                        outline: "none",
                    }}
                >
                    <Box
                        sx={{
                            p: 2.5,
                            bgcolor: "#1a0a0a",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontFamily: '"Cinzel", serif',
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                }}
                            >
                                Export Whitepapers
                            </Typography>
                            <Typography
                                sx={{ fontSize: "0.68rem", opacity: 0.5 }}
                            >
                                Downloads as a formatted .docx
                            </Typography>
                        </Box>
                        <IconButton
                            onClick={() => setExportOpen(false)}
                            sx={{ color: "#fff" }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                    <Box
                        sx={{
                            p: 3,
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                        }}
                    >
                        {exportError && (
                            <Alert
                                severity='error'
                                onClose={() => setExportError("")}
                            >
                                {exportError}
                            </Alert>
                        )}
                        <TextField
                            label='Version Number'
                            value={exportVersion}
                            onChange={(e) => setExportVersion(e.target.value)}
                            fullWidth
                            size='small'
                            helperText={`File will save as imk-whitepapers-v${exportVersion}.docx`}
                        />
                        <Box
                            sx={{
                                p: 1.5,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: "8px",
                                bgcolor: "action.hover",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: "bold",
                                    mb: 0.5,
                                }}
                            >
                                Document format
                            </Typography>
                            <Typography
                                sx={{ fontSize: "0.68rem", opacity: 0.6 }}
                            >
                                Title page · 2-column layout · Garamond + Cinzel
                                fonts · Running header &amp; page numbers
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button
                                onClick={() => setExportOpen(false)}
                                disabled={exporting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                onClick={handleExport}
                                disabled={exporting || !exportVersion.trim()}
                                startIcon={
                                    exporting ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <FileDownload />
                                    )
                                }
                                sx={{
                                    bgcolor: "#8B0000",
                                    "&:hover": { bgcolor: "#a00" },
                                    fontFamily: '"Cinzel", serif',
                                    textTransform: "none",
                                }}
                            >
                                {exporting
                                    ? "Building…"
                                    : `Export v${exportVersion}`}
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Modal>

            {editModal && (
                <Modal
                    open
                    onClose={() => setEditModal(null)}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                    }}
                >
                    <Paper
                        sx={{
                            width: { xs: "calc(100vw - 32px)", sm: 640 },
                            maxWidth: 640,
                            maxHeight: "90vh",
                            display: "flex",
                            flexDirection: "column",
                            borderRadius: "16px",
                            overflow: "hidden",
                            outline: "none",
                        }}
                    >
                        <Box
                            sx={{
                                px: 2.5,
                                py: 1.5,
                                bgcolor: "#1a0a0a",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}
                        >
                            <Box>
                                <Typography
                                    sx={{
                                        fontFamily: '"Cinzel", serif',
                                        fontWeight: "bold",
                                        fontSize: "0.95rem",
                                        color: "#ffa000",
                                    }}
                                >
                                    Edit Rule Section
                                </Typography>
                                <Typography
                                    sx={{ fontSize: "0.68rem", opacity: 0.5 }}
                                >
                                    {editModal.category} / {editModal.sectionId}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={() => setEditModal(null)}
                                sx={{ color: "#fff" }}
                                size='small'
                            >
                                <Close />
                            </IconButton>
                        </Box>

                        <Box
                            sx={{
                                p: 2.5,
                                overflowY: "auto",
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                            }}
                        >
                            {saveMsg && (
                                <Alert
                                    severity={
                                        saveMsg.startsWith("Error")
                                            ? "error"
                                            : "success"
                                    }
                                >
                                    {saveMsg}
                                </Alert>
                            )}
                            <TextField
                                label='Section Title'
                                value={editTitle}
                                onChange={(e) => setEditTitle(e.target.value)}
                                fullWidth
                                size='small'
                            />
                            <TextField
                                label='Section Content (JSON)'
                                value={editJson}
                                onChange={(e) => {
                                    setEditJson(e.target.value)
                                    try {
                                        JSON.parse(e.target.value)
                                        setJsonError("")
                                    } catch (err) {
                                        setJsonError(err.message)
                                    }
                                }}
                                multiline
                                minRows={12}
                                maxRows={22}
                                fullWidth
                                error={Boolean(jsonError)}
                                helperText={jsonError || ""}
                                inputProps={{
                                    style: {
                                        fontFamily: "monospace",
                                        fontSize: "0.75rem",
                                    },
                                }}
                            />
                        </Box>

                        <Box
                            sx={{
                                px: 2.5,
                                py: 1.5,
                                borderTop: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1.5,
                            }}
                        >
                            <Button
                                onClick={() => setEditModal(null)}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant='contained'
                                onClick={handleSave}
                                disabled={saving || Boolean(jsonError)}
                                startIcon={
                                    saving ? (
                                        <CircularProgress
                                            size={14}
                                            color='inherit'
                                        />
                                    ) : (
                                        <Save />
                                    )
                                }
                                sx={{
                                    bgcolor: "#ffa000",
                                    color: "#1a0a0a",
                                    fontWeight: "bold",
                                    "&:hover": { bgcolor: "#ffb300" },
                                }}
                            >
                                {saving ? "Saving…" : "Save Changes"}
                            </Button>
                        </Box>
                    </Paper>
                </Modal>
            )}
        </Box>
    )
}

// ═══════════════════════════════════════════════════════════
// Admin Item Tile
// ═══════════════════════════════════════════════════════════
function AdminItemTile({ item, onEdit, onDelete }) {
    const color = SUBCATEGORY_COLORS[item.subcategory] || "#555"
    return (
        <Box
            sx={{
                width: 130,
                flexShrink: 0,
                "&:hover .item-actions": { opacity: 1 },
                "&:hover .item-paper": {
                    transform: "translateY(-4px) scale(1.04)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                },
            }}
        >
            <Paper
                className='item-paper'
                elevation={3}
                onClick={onEdit}
                sx={{
                    width: 130,
                    height: 195,
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: `2px solid ${color}`,
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    position: "relative",
                    cursor: "pointer",
                }}
            >
                {/* Placeholder art */}
                <Box
                    sx={{
                        height: "60%",
                        bgcolor: `${color}22`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "2rem",
                            opacity: 0.35,
                            lineHeight: 1,
                            userSelect: "none",
                        }}
                    >
                        {item.subcategory === "Weapon"
                            ? "⚔️"
                            : item.subcategory === "Protection"
                              ? "🛡️"
                              : item.subcategory === "Control"
                                ? "⛓️"
                                : "🚫"}
                    </Typography>
                    {/* Hover action overlay */}
                    <Box
                        className='item-actions'
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0,0,0,0.55)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            opacity: 0,
                            transition: "opacity 0.2s",
                        }}
                    >
                        <Tooltip title='Edit'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onEdit()
                                }}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    "&:hover": { bgcolor: color },
                                }}
                            >
                                <Edit sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onDelete()
                                }}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "#c62828" },
                                }}
                            >
                                <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                {/* Info */}
                <Box
                    sx={{
                        height: "40%",
                        p: 0.75,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "0.58rem",
                            lineHeight: 1.2,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                        }}
                    >
                        {item.name}
                    </Typography>
                    <Chip
                        label={item.tier}
                        size='small'
                        sx={{
                            height: 12,
                            fontSize: "0.35rem",
                            fontWeight: "bold",
                            bgcolor:
                                item.tier === "High Pay" ? "#ffa000" : color,
                            color:
                                item.tier === "High Pay" ? "#1a0a0a" : "#fff",
                            alignSelf: "flex-start",
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    )
}

// ═══════════════════════════════════════════════════════════
// Admin Equipment Panel
// ═══════════════════════════════════════════════════════════
function AdminEquipmentPanel() {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [search, setSearch] = useState("")
    const [subFilter, setSubFilter] = useState("All")
    const [itemModal, setItemModal] = useState({ open: false, item: null })
    const [deleteModal, setDeleteModal] = useState({ open: false, item: null })
    const [deleting, setDeleting] = useState(false)
    const [csvImportOpen, setCsvImportOpen] = useState(false)

    const loadItems = useCallback(async () => {
        setLoading(true)
        setError("")
        try {
            const data = await adminFetch("/equipment")
            setItems(data.equipment || [])
        } catch (e) {
            setError("Failed to load equipment: " + e.message)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        loadItems()
    }, [loadItems])

    const saveItem = async (itemData, originalItem) => {
        if (originalItem) {
            const pkChanged = itemData.subcategory !== originalItem.subcategory
            const skChanged = itemData.name !== originalItem.name
            if (pkChanged || skChanged) {
                await adminFetch(
                    `/equipment/${encodeURIComponent(originalItem.subcategory)}/${encodeURIComponent(originalItem.name)}`,
                    "DELETE",
                )
                await adminFetch("/equipment", "POST", itemData)
            } else {
                await adminFetch(
                    `/equipment/${encodeURIComponent(originalItem.subcategory)}/${encodeURIComponent(originalItem.name)}`,
                    "PUT",
                    itemData,
                )
            }
        } else {
            await adminFetch("/equipment", "POST", itemData)
        }
        await loadItems()
    }

    const handleDelete = async () => {
        setDeleting(true)
        try {
            const { subcategory, name } = deleteModal.item
            await adminFetch(
                `/equipment/${encodeURIComponent(subcategory)}/${encodeURIComponent(name)}`,
                "DELETE",
            )
            setDeleteModal({ open: false, item: null })
            await loadItems()
        } catch (e) {
            setError("Delete failed: " + e.message)
        } finally {
            setDeleting(false)
        }
    }

    const importEquipment = async (itemsToImport) => {
        const result = await adminFetch("/equipment/batch", "POST", {
            equipment: itemsToImport,
        })
        await loadItems()
        return result
    }

    const q = search.trim().toLowerCase()
    const filtered = items.filter((item) => {
        const matchSub = subFilter === "All" || item.subcategory === subFilter
        const matchQ =
            !q ||
            item.name.toLowerCase().includes(q) ||
            (item.description || "").toLowerCase().includes(q)
        return matchSub && matchQ
    })

    // Sorted: subcategory order → Low Pay before High Pay → name A-Z
    const sorted = [...filtered].sort((a, b) => {
        const subDiff =
            SUBCATEGORY_ORDER.indexOf(a.subcategory) -
            SUBCATEGORY_ORDER.indexOf(b.subcategory)
        if (subDiff !== 0) return subDiff
        const tierDiff =
            (a.tier === "High Pay" ? 1 : 0) - (b.tier === "High Pay" ? 1 : 0)
        if (tierDiff !== 0) return tierDiff
        return a.name.localeCompare(b.name)
    })

    const totalItems = items.length
    const filterTabs = ["All", ...SUBCATEGORY_ORDER]

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                        }}
                    >
                        Hunter's Arsenal
                    </Typography>
                    <Typography sx={{ fontSize: "0.72rem", opacity: 0.5 }}>
                        {totalItems} item{totalItems !== 1 ? "s" : ""} across{" "}
                        {SUBCATEGORY_ORDER.length} subcategories
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        size='small'
                        variant='outlined'
                        onClick={loadItems}
                        disabled={loading}
                        sx={{ fontSize: "0.72rem", textTransform: "none" }}
                    >
                        Refresh
                    </Button>
                    <Button
                        size='small'
                        variant='outlined'
                        startIcon={<FileDownload sx={{ fontSize: 14 }} />}
                        disabled={items.length === 0}
                        onClick={() => exportEquipmentAsCsv(items)}
                        sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            fontFamily: '"Cinzel", serif',
                        }}
                    >
                        Export CSV
                    </Button>
                    <Button
                        size='small'
                        variant='outlined'
                        startIcon={<FileUpload sx={{ fontSize: 14 }} />}
                        onClick={() => setCsvImportOpen(true)}
                        sx={{
                            fontSize: "0.72rem",
                            textTransform: "none",
                            fontFamily: '"Cinzel", serif',
                        }}
                    >
                        Import CSV
                    </Button>
                    <Button
                        variant='contained'
                        size='small'
                        startIcon={<Add />}
                        onClick={() => setItemModal({ open: true, item: null })}
                        sx={{
                            bgcolor: "#8B0000",
                            "&:hover": { bgcolor: "#a00" },
                            textTransform: "none",
                            fontFamily: '"Cinzel", serif',
                            fontSize: "0.75rem",
                        }}
                    >
                        New Item
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert
                    severity='error'
                    onClose={() => setError("")}
                    sx={{ mb: 2 }}
                >
                    {error}
                </Alert>
            )}

            {/* Search + subcategory filter */}
            <Box
                sx={{
                    display: "flex",
                    gap: 1,
                    mb: 2,
                    flexWrap: "wrap",
                    alignItems: "center",
                }}
            >
                <TextField
                    placeholder='Search items…'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size='small'
                    sx={{ flex: 1, minWidth: 180 }}
                />
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {filterTabs.map((tab) => (
                        <Chip
                            key={tab}
                            label={tab}
                            size='small'
                            onClick={() => setSubFilter(tab)}
                            sx={{
                                cursor: "pointer",
                                bgcolor:
                                    subFilter === tab
                                        ? SUBCATEGORY_COLORS[tab] || "#555"
                                        : "transparent",
                                color: subFilter === tab ? "#fff" : "inherit",
                                border: "1px solid",
                                borderColor:
                                    subFilter === tab
                                        ? SUBCATEGORY_COLORS[tab] || "#555"
                                        : "divider",
                                fontWeight:
                                    subFilter === tab ? "bold" : "normal",
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* Items grid */}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
                    <CircularProgress />
                </Box>
            ) : sorted.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                        p: 6,
                        border: "2px dashed",
                        borderColor: "divider",
                        borderRadius: "16px",
                        cursor: "pointer",
                        "&:hover": {
                            borderColor: "#8B0000",
                            bgcolor: "rgba(139,0,0,0.02)",
                        },
                    }}
                    onClick={() => setItemModal({ open: true, item: null })}
                >
                    <Add sx={{ fontSize: 40, opacity: 0.3 }} />
                    <Typography
                        sx={{ opacity: 0.4, fontFamily: '"Cinzel", serif' }}
                    >
                        No items found — click to add one
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        alignItems: "flex-start",
                    }}
                >
                    {sorted.map((item) => (
                        <AdminItemTile
                            key={`${item.subcategory}/${item.name}`}
                            item={item}
                            onEdit={() => setItemModal({ open: true, item })}
                            onDelete={() =>
                                setDeleteModal({ open: true, item })
                            }
                        />
                    ))}
                </Box>
            )}

            <ItemFormModal
                open={itemModal.open}
                onClose={() => setItemModal({ open: false, item: null })}
                onSave={saveItem}
                initialItem={itemModal.item}
            />
            <EquipmentCsvImportModal
                open={csvImportOpen}
                onClose={() => setCsvImportOpen(false)}
                onImport={importEquipment}
            />
            <ConfirmDeleteModal
                open={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, item: null })}
                onConfirm={handleDelete}
                loading={deleting}
                title='Delete Item'
                message={`Are you sure? This will permanently delete "${deleteModal.item?.name}".`}
            />
        </Box>
    )
}

// ═══════════════════════════════════════════════════════════
// Admin Nav Panel
// ═══════════════════════════════════════════════════════════
function AdminNavPanel() {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [msg, setMsg] = useState("")
    const [newLabel, setNewLabel] = useState("")
    const [newPath, setNewPath] = useState("")

    // Load: DB first, then static JSON
    useEffect(() => {
        const load = async () => {
            setLoading(true)
            try {
                const dbItems = await getNavConfig()
                if (dbItems) {
                    setItems(
                        dbItems
                            .slice()
                            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
                    )
                    setLoading(false)
                    return
                }
                const res = await fetch("/nav-config.json")
                const data = await res.json()
                setItems(
                    (data.navItems || [])
                        .slice()
                        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
                )
            } catch (e) {
                setMsg("❌ Failed to load nav config: " + e.message)
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [])

    const move = (idx, dir) => {
        const next = [...items]
        const swapIdx = idx + dir
        if (swapIdx < 0 || swapIdx >= next.length) return
        ;[next[idx], next[swapIdx]] = [next[swapIdx], next[idx]]
        setItems(next.map((item, i) => ({ ...item, order: i })))
    }

    const toggleVisible = (idx) => {
        setItems((prev) =>
            prev.map((item, i) =>
                i === idx
                    ? { ...item, visible: !(item.visible ?? true) }
                    : item,
            ),
        )
    }

    const removeItem = (idx) => {
        setItems((prev) =>
            prev
                .filter((_, i) => i !== idx)
                .map((item, i) => ({ ...item, order: i })),
        )
    }

    const addItem = () => {
        if (!newLabel.trim() || !newPath.trim()) return
        const id = newLabel
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-|-$/g, "")
        setItems((prev) => [
            ...prev,
            {
                id,
                label: newLabel.trim(),
                path: newPath.trim().startsWith("/")
                    ? newPath.trim()
                    : "/" + newPath.trim(),
                order: prev.length,
                visible: true,
            },
        ])
        setNewLabel("")
        setNewPath("")
    }

    const handleSave = async () => {
        setSaving(true)
        setMsg("")
        try {
            const idToken = localStorage.getItem("imk_admin_id_token")
            const normalised = items.map((item, i) => ({ ...item, order: i }))
            await saveNavConfig(normalised, idToken)
            setMsg("✅ Nav config saved! Reload the home page to see changes.")
        } catch (e) {
            setMsg("❌ Save failed: " + e.message)
        } finally {
            setSaving(false)
        }
    }

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 640 }}>
            <Typography
                sx={{
                    fontFamily: '"Cinzel", serif',
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    mb: 0.5,
                }}
            >
                Nav Pages
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", opacity: 0.5, mb: 2 }}>
                Reorder, hide, or add pages. Changes are saved to the database
                and reflected live on the home page.
            </Typography>

            {msg && (
                <Alert
                    severity={msg.startsWith("✅") ? "success" : "error"}
                    onClose={() => setMsg("")}
                    sx={{ mb: 2 }}
                >
                    {msg}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        mb: 3,
                    }}
                >
                    {items.map((item, idx) => (
                        <Paper
                            key={item.id}
                            variant='outlined'
                            sx={{
                                p: 1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                opacity: item.visible === false ? 0.45 : 1,
                                borderColor:
                                    item.visible === false
                                        ? "divider"
                                        : "rgba(255,160,0,0.3)",
                            }}
                        >
                            {/* Reorder */}
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    mr: 0.5,
                                }}
                            >
                                <IconButton
                                    size='small'
                                    onClick={() => move(idx, -1)}
                                    disabled={idx === 0}
                                    sx={{ p: 0.3 }}
                                >
                                    <ArrowUpward sx={{ fontSize: 13 }} />
                                </IconButton>
                                <IconButton
                                    size='small'
                                    onClick={() => move(idx, 1)}
                                    disabled={idx === items.length - 1}
                                    sx={{ p: 0.3 }}
                                >
                                    <ArrowDownward sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Box>

                            {/* Label + path */}
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontWeight: "bold",
                                        fontSize: "0.85rem",
                                    }}
                                >
                                    {item.label}
                                </Typography>
                                <Typography
                                    sx={{ fontSize: "0.68rem", opacity: 0.5 }}
                                >
                                    {item.path}
                                </Typography>
                            </Box>

                            {/* Visibility toggle */}
                            <Tooltip
                                title={
                                    item.visible === false
                                        ? "Show in menu"
                                        : "Hide from menu"
                                }
                            >
                                <IconButton
                                    size='small'
                                    onClick={() => toggleVisible(idx)}
                                    sx={{ opacity: 0.7 }}
                                >
                                    {item.visible === false ? (
                                        <VisibilityOff sx={{ fontSize: 16 }} />
                                    ) : (
                                        <Visibility sx={{ fontSize: 16 }} />
                                    )}
                                </IconButton>
                            </Tooltip>

                            {/* Delete */}
                            <Tooltip title='Remove'>
                                <IconButton
                                    size='small'
                                    onClick={() => removeItem(idx)}
                                    sx={{ color: "#e57373", opacity: 0.7 }}
                                >
                                    <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* Add new item */}
            <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
                <Typography
                    sx={{
                        fontFamily: '"Cinzel", serif',
                        fontSize: "0.78rem",
                        fontWeight: "bold",
                        mb: 1.5,
                        opacity: 0.7,
                    }}
                >
                    ADD PAGE
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <TextField
                        label='Label'
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        size='small'
                        sx={{ flex: 2, minWidth: 150 }}
                        placeholder='Combat Mechanics'
                    />
                    <TextField
                        label='Path'
                        value={newPath}
                        onChange={(e) => setNewPath(e.target.value)}
                        size='small'
                        sx={{ flex: 2, minWidth: 150 }}
                        placeholder='/combat-mechanics'
                    />
                    <Button
                        variant='outlined'
                        onClick={addItem}
                        disabled={!newLabel.trim() || !newPath.trim()}
                        startIcon={<Add />}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        Add
                    </Button>
                </Box>
            </Paper>

            <Button
                variant='contained'
                onClick={handleSave}
                disabled={saving || loading}
                startIcon={
                    saving ? (
                        <CircularProgress size={14} color='inherit' />
                    ) : (
                        <Save />
                    )
                }
                sx={{
                    bgcolor: "#ffa000",
                    color: "#1a0a0a",
                    fontWeight: "bold",
                    "&:hover": { bgcolor: "#ffb300" },
                }}
            >
                {saving ? "Saving…" : "Save Nav Config"}
            </Button>
        </Box>
    )
}

// ═══════════════════════════════════════════════════════════
// Dashboard
// ═══════════════════════════════════════════════════════════
function AdminDashboard({ email, onLogout }) {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const navigate = useNavigate()
    const { deckName: urlDeckName } = useParams()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [activeView, setActiveView] = useState("cards")
    const [decks, setDecks] = useState([])
    const [selectedDeck, setSelectedDeck] = useState(null)
    const [cards, setCards] = useState([])
    const [cardSearch, setCardSearch] = useState("")
    const [cardSort, setCardSort] = useState("name")
    const [artCacheBust, setArtCacheBust] = useState(null)
    const [loading, setLoading] = useState({
        decks: true,
        cards: false,
        deleting: false,
    })
    const [error, setError] = useState("")

    // Modal state
    const [deckModal, setDeckModal] = useState({ open: false, deck: null })
    const [cardModal, setCardModal] = useState({
        open: false,
        card: null,
        index: null,
    })
    const [csvImportOpen, setCsvImportOpen] = useState(false)
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        type: null,
        target: null,
    })

    // ── Load decks ────────────────────────────────────────────────────────────
    const loadDecks = useCallback(async () => {
        setLoading((l) => ({ ...l, decks: true }))
        try {
            const { decks: d } = await adminFetch("/admin/decks")
            setDecks(
                d.sort((a, b) => a.displayName.localeCompare(b.displayName)),
            )
        } catch (e) {
            setError("Failed to load decks: " + e.message)
        } finally {
            setLoading((l) => ({ ...l, decks: false }))
        }
    }, [])

    // ── Load cards for selected deck ──────────────────────────────────────────
    const loadCards = useCallback(async (deck) => {
        setLoading((l) => ({ ...l, cards: true }))
        try {
            const data = await fetch(
                `${API_URL}/cards?deck=${encodeURIComponent(deck.deck)}`,
            ).then((r) => r.json())
            const deckCards = (data.cards || []).filter(
                (c) => c.name !== "__meta__",
            )
            setCards(deckCards)
        } catch (e) {
            setError("Failed to load cards: " + e.message)
        } finally {
            setLoading((l) => ({ ...l, cards: false }))
        }
    }, [])

    useEffect(() => {
        loadDecks()
    }, [loadDecks])
    useEffect(() => {
        if (selectedDeck) loadCards(selectedDeck)
    }, [selectedDeck, loadCards])

    // Auto-select deck from URL param once decks are loaded
    useEffect(() => {
        if (urlDeckName && decks.length > 0 && !selectedDeck) {
            const match = decks.find(
                (d) => d.deck === decodeURIComponent(urlDeckName),
            )
            if (match) setSelectedDeck(match)
        }
    }, [urlDeckName, decks, selectedDeck])

    // ── Deck CRUD ─────────────────────────────────────────────────────────────
    const saveDeck = async (data) => {
        if (deckModal.deck) {
            await adminFetch(
                `/admin/decks/${encodeURIComponent(data.deck)}`,
                "PUT",
                data,
            )
        } else {
            await adminFetch("/admin/decks", "POST", data)
        }
        await loadDecks()
        if (selectedDeck?.deck === data.deck) setSelectedDeck(data)
    }

    const deleteDeck = async () => {
        setLoading((l) => ({ ...l, deleting: true }))
        try {
            await adminFetch(
                `/admin/decks/${encodeURIComponent(deleteModal.target.deck)}`,
                "DELETE",
            )
            setDeleteModal({ open: false })
            if (selectedDeck?.deck === deleteModal.target.deck) {
                setSelectedDeck(null)
                setCards([])
                navigate("/admin")
            }
            await loadDecks()
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading((l) => ({ ...l, deleting: false }))
        }
    }

    // ── Card CRUD ─────────────────────────────────────────────────────────────
    const saveCard = async (cardData, isEdit) => {
        if (isEdit) {
            await adminFetch(
                `/cards/${encodeURIComponent(cardData.deck)}/${encodeURIComponent(cardData.name)}`,
                "PUT",
                cardData,
            )
        } else {
            await adminFetch("/cards", "POST", cardData)
        }
        setArtCacheBust(Date.now())
        await loadCards(selectedDeck)
    }

    const deleteCard = async () => {
        setLoading((l) => ({ ...l, deleting: true }))
        try {
            await adminFetch(
                `/cards/${encodeURIComponent(deleteModal.target.deck)}/${encodeURIComponent(deleteModal.target.name)}`,
                "DELETE",
            )
            setDeleteModal({ open: false })
            await loadCards(selectedDeck)
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading((l) => ({ ...l, deleting: false }))
        }
    }

    const importCards = async (cardsToImport) => {
        const result = await adminFetch("/cards/batch", "POST", {
            cards: cardsToImport,
        })
        setArtCacheBust(Date.now())
        await loadCards(selectedDeck)
        return result
    }

    const powerDecks = decks.filter((d) => d.cardType === "power")
    const monsterDecks = decks.filter((d) => d.cardType === "monster")

    const displayedCards = useMemo(() => {
        const q = cardSearch.trim().toLowerCase()
        const filtered = cards.filter((c) => {
            if (!q) return true
            const name = String(c.name || "").toLowerCase()
            const rarity = String(c.rarity || "").toLowerCase()
            const desc = String(c.description || "").toLowerCase()
            return name.includes(q) || rarity.includes(q) || desc.includes(q)
        })

        const order = { common: 1, uncommon: 2, rare: 3, mythic: 4 }

        if (cardSort === "rarityAsc") {
            return [...filtered].sort((a, b) => {
                const aRarity = String(a.rarity || "").toLowerCase()
                const bRarity = String(b.rarity || "").toLowerCase()
                const aVal = order[aRarity] ?? 999
                const bVal = order[bRarity] ?? 999
                if (aVal !== bVal) return aVal - bVal
                return String(a.name || "").localeCompare(String(b.name || ""))
            })
        }

        if (cardSort === "rarityDesc") {
            return [...filtered].sort((a, b) => {
                const aRarity = String(a.rarity || "").toLowerCase()
                const bRarity = String(b.rarity || "").toLowerCase()
                const aVal = order[aRarity] ?? 999
                const bVal = order[bRarity] ?? 999
                if (aVal !== bVal) return bVal - aVal
                return String(a.name || "").localeCompare(String(b.name || ""))
            })
        }

        return [...filtered].sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || "")),
        )
    }, [cards, cardSearch, cardSort])

    // ── Reusable sidebar content (used in both desktop sidebar and mobile Drawer) ──
    const renderSidebarBody = (onDeckSelect = () => {}) =>
        loading.decks ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={24} />
            </Box>
        ) : (
            <>
                {/* Rules Database */}
                <Box
                    onClick={() => {
                        setActiveView((v) =>
                            v === "rules" ? "cards" : "rules",
                        )
                        setSelectedDeck(null)
                        navigate("/admin")
                        onDeckSelect()
                    }}
                    sx={{
                        mx: 2,
                        mt: 2,
                        mb: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: "10px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid",
                        borderColor:
                            activeView === "rules" ? "#ffa000" : "divider",
                        bgcolor:
                            activeView === "rules"
                                ? "rgba(255,160,0,0.08)"
                                : "transparent",
                        "&:hover": {
                            bgcolor: "rgba(255,160,0,0.06)",
                            borderColor: "#ffa000",
                        },
                        transition: "all 0.15s",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "0.78rem",
                            color:
                                activeView === "rules" ? "#ffa000" : "inherit",
                        }}
                    >
                        📜 Rules Database
                    </Typography>
                    {activeView === "rules" && (
                        <Chip
                            label='Active'
                            size='small'
                            sx={{
                                height: 16,
                                fontSize: "0.6rem",
                                bgcolor: "#ffa000",
                                color: "#1a0a0a",
                                fontWeight: "bold",
                            }}
                        />
                    )}
                </Box>

                {/* Equipment */}
                <Box
                    onClick={() => {
                        setActiveView((v) =>
                            v === "equipment" ? "cards" : "equipment",
                        )
                        setSelectedDeck(null)
                        navigate("/admin")
                        onDeckSelect()
                    }}
                    sx={{
                        mx: 2,
                        mt: 1,
                        mb: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: "10px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        border: "1px solid",
                        borderColor:
                            activeView === "equipment" ? "#8B0000" : "divider",
                        bgcolor:
                            activeView === "equipment"
                                ? "rgba(139,0,0,0.08)"
                                : "transparent",
                        "&:hover": {
                            bgcolor: "rgba(139,0,0,0.06)",
                            borderColor: "#8B0000",
                        },
                        transition: "all 0.15s",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "0.78rem",
                            color:
                                activeView === "equipment"
                                    ? "#c62828"
                                    : "inherit",
                        }}
                    >
                        ⚔️ Armory
                    </Typography>
                    {activeView === "equipment" && (
                        <Chip
                            label='Active'
                            size='small'
                            sx={{
                                height: 16,
                                fontSize: "0.6rem",
                                bgcolor: "#8B0000",
                                color: "#fff",
                                fontWeight: "bold",
                            }}
                        />
                    )}
                </Box>

                {/* Power Decks */}
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Cinzel", serif',
                                fontWeight: "bold",
                                fontSize: "0.75rem",
                                opacity: 0.5,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                            }}
                        >
                            ⚡ Power Decks
                        </Typography>
                        <Tooltip title='New Power Deck'>
                            <IconButton
                                size='small'
                                onClick={() =>
                                    setDeckModal({
                                        open: true,
                                        deck: null,
                                        defaultType: "power",
                                    })
                                }
                                sx={{
                                    opacity: 0.6,
                                    "&:hover": { opacity: 1, color: "#8B0000" },
                                }}
                            >
                                <Add sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {powerDecks.length === 0 && (
                        <Typography
                            sx={{ fontSize: "0.75rem", opacity: 0.3, pl: 1 }}
                        >
                            No power decks yet
                        </Typography>
                    )}
                    {powerDecks.map((deck) => (
                        <DeckItem
                            key={deck.deck}
                            deck={deck}
                            selected={selectedDeck?.deck === deck.deck}
                            onClick={() => {
                                setSelectedDeck(deck)
                                navigate(
                                    "/admin/" + encodeURIComponent(deck.deck),
                                )
                                onDeckSelect()
                            }}
                            onEdit={() => setDeckModal({ open: true, deck })}
                            onDelete={() =>
                                setDeleteModal({
                                    open: true,
                                    type: "deck",
                                    target: deck,
                                })
                            }
                        />
                    ))}
                </Box>
                <Divider />
                {/* Monster Decks */}
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Cinzel", serif',
                                fontWeight: "bold",
                                fontSize: "0.75rem",
                                opacity: 0.5,
                                textTransform: "uppercase",
                                letterSpacing: 1,
                            }}
                        >
                            💀 Monster Decks
                        </Typography>
                        <Tooltip title='New Monster Deck'>
                            <IconButton
                                size='small'
                                onClick={() =>
                                    setDeckModal({
                                        open: true,
                                        deck: null,
                                        defaultType: "monster",
                                    })
                                }
                                sx={{
                                    opacity: 0.6,
                                    "&:hover": { opacity: 1, color: "#8B0000" },
                                }}
                            >
                                <Add sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    {monsterDecks.length === 0 && (
                        <Typography
                            sx={{ fontSize: "0.75rem", opacity: 0.3, pl: 1 }}
                        >
                            No monster decks yet
                        </Typography>
                    )}
                    {monsterDecks.map((deck) => (
                        <DeckItem
                            key={deck.deck}
                            deck={deck}
                            selected={selectedDeck?.deck === deck.deck}
                            onClick={() => {
                                setSelectedDeck(deck)
                                navigate(
                                    "/admin/" + encodeURIComponent(deck.deck),
                                )
                                onDeckSelect()
                            }}
                            onEdit={() => setDeckModal({ open: true, deck })}
                            onDelete={() =>
                                setDeleteModal({
                                    open: true,
                                    type: "deck",
                                    target: deck,
                                })
                            }
                        />
                    ))}
                </Box>
            </>
        )

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? "#0d0606" : "#f5f0eb",
            }}
        >
            {/* Top Bar */}
            <Box
                sx={{
                    px: 3,
                    py: 1.5,
                    bgcolor: "#1a0a0a",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    boxShadow: "0 2px 12px rgba(0,0,0,0.5)",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {isMobile && (
                        <IconButton
                            onClick={() => setSidebarOpen(true)}
                            sx={{ color: "#fff", mr: 0.5 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <D10Icon size={28} />
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel Decorative", "Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: { xs: "0.85rem", sm: "1.1rem" },
                            letterSpacing: 1,
                        }}
                    >
                        I MUST KILL — Admin
                    </Typography>
                    <Chip
                        label='Card Manager'
                        size='small'
                        sx={{
                            display: { xs: "none", sm: "flex" },
                            bgcolor: "rgba(139,0,0,0.5)",
                            color: "#ffcdd2",
                            fontSize: "0.65rem",
                        }}
                    />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography
                        sx={{
                            display: { xs: "none", sm: "block" },
                            opacity: 0.6,
                            fontSize: "0.8rem",
                        }}
                    >
                        {email}
                    </Typography>
                    <Tooltip title='Sign Out'>
                        <IconButton
                            onClick={onLogout}
                            sx={{
                                color: "#fff",
                                "&:hover": { color: "#ff6b6b" },
                            }}
                        >
                            <Logout />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert
                    severity='error'
                    onClose={() => setError("")}
                    sx={{ mx: 3, mt: 2 }}
                >
                    {error}
                </Alert>
            )}

            <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                {/* ── Mobile Sidebar Drawer ── */}
                <Drawer
                    anchor='left'
                    open={isMobile && sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    PaperProps={{
                        sx: {
                            width: 280,
                            bgcolor: (theme) =>
                                theme.palette.mode === "dark"
                                    ? "#1a0a0a"
                                    : "#fff",
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 2,
                            py: 1.5,
                            bgcolor: "#1a0a0a",
                            color: "#fff",
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: '"Cinzel", serif',
                                fontWeight: "bold",
                                fontSize: "0.9rem",
                                color: "#fff",
                            }}
                        >
                            Decks
                        </Typography>
                        <IconButton
                            onClick={() => setSidebarOpen(false)}
                            sx={{ color: "#fff" }}
                        >
                            <Close />
                        </IconButton>
                    </Box>
                    <Divider />
                    {renderSidebarBody(() => setSidebarOpen(false))}
                </Drawer>

                {/* ── Sidebar (desktop) ── */}
                <Box
                    sx={{
                        width: 260,
                        flexShrink: 0,
                        borderRight: "1px solid",
                        borderColor: "divider",
                        display: { xs: "none", sm: "flex" },
                        flexDirection: "column",
                        overflow: "auto",
                        bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                                ? "rgba(0,0,0,0.4)"
                                : "rgba(255,255,255,0.6)",
                    }}
                >
                    {renderSidebarBody()}
                </Box>

                {/* ── Main Content ── */}
                <Box sx={{ flex: 1, overflow: "auto", p: { xs: 2, sm: 3 } }}>
                    {activeView === "nav" ? (
                        <AdminNavPanel />
                    ) : activeView === "rules" ? (
                        <AdminRulesPanel />
                    ) : activeView === "equipment" ? (
                        <AdminEquipmentPanel />
                    ) : !selectedDeck ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                opacity: 0.4,
                                gap: 2,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"Cinzel", serif',
                                    fontSize: "1rem",
                                }}
                            >
                                Select a deck from the sidebar, or create a new
                                one
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Deck Header */}
                            <Box
                                sx={{
                                    mb: 3,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1.5,
                                            mb: 0.5,
                                        }}
                                    >
                                        <Typography
                                            variant='h5'
                                            sx={{
                                                fontFamily: '"Cinzel", serif',
                                                fontWeight: "bold",
                                                fontSize: {
                                                    xs: "1rem",
                                                    sm: "1.5rem",
                                                },
                                            }}
                                        >
                                            {selectedDeck.displayName}
                                        </Typography>
                                        <Chip
                                            label={
                                                selectedDeck.cardType ===
                                                "monster"
                                                    ? "Monster"
                                                    : "Power"
                                            }
                                            size='small'
                                            icon={
                                                selectedDeck.cardType ===
                                                "monster" ? (
                                                    <BugReport
                                                        sx={{ fontSize: 12 }}
                                                    />
                                                ) : (
                                                    <AutoAwesome
                                                        sx={{ fontSize: 12 }}
                                                    />
                                                )
                                            }
                                            sx={{
                                                bgcolor:
                                                    selectedDeck.cardType ===
                                                    "monster"
                                                        ? "rgba(198,40,40,0.15)"
                                                        : "rgba(142,36,170,0.15)",
                                                color:
                                                    selectedDeck.cardType ===
                                                    "monster"
                                                        ? "#c62828"
                                                        : "#8e24aa",
                                            }}
                                        />
                                        <Chip
                                            label={`${cards.length} card${cards.length !== 1 ? "s" : ""}`}
                                            size='small'
                                            variant='outlined'
                                        />
                                    </Box>
                                    {selectedDeck.description && (
                                        <Typography
                                            sx={{
                                                opacity: 0.6,
                                                fontSize: "0.85rem",
                                            }}
                                        >
                                            {selectedDeck.description}
                                        </Typography>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {isMobile ? (
                                        // Mobile: icon-only buttons
                                        <>
                                            <Tooltip title='Edit Deck'>
                                                <IconButton
                                                    size='small'
                                                    onClick={() =>
                                                        setDeckModal({
                                                            open: true,
                                                            deck: selectedDeck,
                                                        })
                                                    }
                                                >
                                                    <Edit
                                                        sx={{ fontSize: 18 }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title='Export CSV'>
                                                <span>
                                                    <IconButton
                                                        size='small'
                                                        disabled={
                                                            cards.length === 0
                                                        }
                                                        onClick={() =>
                                                            exportCardsAsCsv(
                                                                cards,
                                                                selectedDeck.cardType,
                                                                selectedDeck.displayName,
                                                            )
                                                        }
                                                    >
                                                        <FileDownload
                                                            sx={{
                                                                fontSize: 18,
                                                            }}
                                                        />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title='Import CSV'>
                                                <IconButton
                                                    size='small'
                                                    onClick={() =>
                                                        setCsvImportOpen(true)
                                                    }
                                                >
                                                    <FileUpload
                                                        sx={{ fontSize: 18 }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title='Delete Deck'>
                                                <IconButton
                                                    size='small'
                                                    color='error'
                                                    onClick={() =>
                                                        setDeleteModal({
                                                            open: true,
                                                            type: "deck",
                                                            target: selectedDeck,
                                                        })
                                                    }
                                                >
                                                    <Delete
                                                        sx={{ fontSize: 18 }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title='New Card'>
                                                <IconButton
                                                    size='small'
                                                    onClick={() =>
                                                        setCardModal({
                                                            open: true,
                                                            card: null,
                                                        })
                                                    }
                                                    sx={{
                                                        bgcolor: "#8B0000",
                                                        color: "#fff",
                                                        "&:hover": {
                                                            bgcolor: "#a00",
                                                        },
                                                    }}
                                                >
                                                    <Add
                                                        sx={{ fontSize: 18 }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                size='small'
                                                startIcon={
                                                    <Edit
                                                        sx={{ fontSize: 14 }}
                                                    />
                                                }
                                                onClick={() =>
                                                    setDeckModal({
                                                        open: true,
                                                        deck: selectedDeck,
                                                    })
                                                }
                                                sx={{
                                                    textTransform: "none",
                                                    fontFamily:
                                                        '"Cinzel", serif',
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Edit Deck
                                            </Button>
                                            <Button
                                                size='small'
                                                startIcon={
                                                    <FileDownload
                                                        sx={{ fontSize: 14 }}
                                                    />
                                                }
                                                disabled={cards.length === 0}
                                                onClick={() =>
                                                    exportCardsAsCsv(
                                                        cards,
                                                        selectedDeck.cardType,
                                                        selectedDeck.displayName,
                                                    )
                                                }
                                                sx={{
                                                    textTransform: "none",
                                                    fontFamily:
                                                        '"Cinzel", serif',
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Export CSV
                                            </Button>
                                            <Button
                                                size='small'
                                                startIcon={
                                                    <FileUpload
                                                        sx={{ fontSize: 14 }}
                                                    />
                                                }
                                                onClick={() =>
                                                    setCsvImportOpen(true)
                                                }
                                                sx={{
                                                    textTransform: "none",
                                                    fontFamily:
                                                        '"Cinzel", serif',
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Import CSV
                                            </Button>
                                            <Button
                                                size='small'
                                                color='error'
                                                startIcon={
                                                    <Delete
                                                        sx={{ fontSize: 14 }}
                                                    />
                                                }
                                                onClick={() =>
                                                    setDeleteModal({
                                                        open: true,
                                                        type: "deck",
                                                        target: selectedDeck,
                                                    })
                                                }
                                                sx={{
                                                    textTransform: "none",
                                                    fontFamily:
                                                        '"Cinzel", serif',
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                Delete Deck
                                            </Button>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 2,
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <TextField
                                                    size='small'
                                                    value={cardSearch}
                                                    onChange={(e) =>
                                                        setCardSearch(
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder='Search cards…'
                                                    sx={{ minWidth: 220 }}
                                                />
                                                <FormControl
                                                    size='small'
                                                    sx={{ minWidth: 180 }}
                                                >
                                                    <InputLabel>
                                                        Sort by
                                                    </InputLabel>
                                                    <Select
                                                        value={cardSort}
                                                        label='Sort by'
                                                        onChange={(e) =>
                                                            setCardSort(
                                                                e.target.value,
                                                            )
                                                        }
                                                    >
                                                        <MenuItem value='name'>
                                                            Name (A–Z)
                                                        </MenuItem>
                                                        <MenuItem value='rarityAsc'>
                                                            Rarity (Low → High)
                                                        </MenuItem>
                                                        <MenuItem value='rarityDesc'>
                                                            Rarity (High → Low)
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                            <Button
                                                variant='contained'
                                                size='small'
                                                startIcon={<Add />}
                                                onClick={() =>
                                                    setCardModal({
                                                        open: true,
                                                        card: null,
                                                    })
                                                }
                                                sx={{
                                                    bgcolor: "#8B0000",
                                                    "&:hover": {
                                                        bgcolor: "#a00",
                                                    },
                                                    textTransform: "none",
                                                    fontFamily:
                                                        '"Cinzel", serif',
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                New Card
                                            </Button>
                                        </>
                                    )}
                                </Box>
                            </Box>

                            {/* Cards Grid */}
                            {loading.cards ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        p: 6,
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            ) : displayedCards.length === 0 ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 2,
                                        p: 6,
                                        border: "2px dashed",
                                        borderColor: "divider",
                                        borderRadius: "16px",
                                        cursor: "pointer",
                                        "&:hover": {
                                            borderColor: "#8B0000",
                                            bgcolor: "rgba(139,0,0,0.02)",
                                        },
                                    }}
                                    onClick={() =>
                                        setCardModal({ open: true, card: null })
                                    }
                                >
                                    <Add sx={{ fontSize: 40, opacity: 0.3 }} />
                                    <Typography
                                        sx={{
                                            opacity: 0.4,
                                            fontFamily: '"Cinzel", serif',
                                        }}
                                    >
                                        No cards match your filter
                                    </Typography>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 2,
                                        alignItems: "flex-start",
                                    }}
                                >
                                    {displayedCards.map((card) => (
                                        <AdminCard
                                            key={`${card.deck}/${card.name}`}
                                            card={card}
                                            deckId={selectedDeck.deck}
                                            deckType={selectedDeck.cardType}
                                            artCacheBust={artCacheBust}
                                            onEdit={() => {
                                                const idx =
                                                    displayedCards.findIndex(
                                                        (c) =>
                                                            c.deck ===
                                                                card.deck &&
                                                            c.name ===
                                                                card.name,
                                                    )
                                                setCardModal({
                                                    open: true,
                                                    card,
                                                    index: idx >= 0 ? idx : 0,
                                                })
                                            }}
                                            onDelete={() =>
                                                setDeleteModal({
                                                    open: true,
                                                    type: "card",
                                                    target: card,
                                                })
                                            }
                                        />
                                    ))}
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <DeckFormModal
                open={deckModal.open}
                onClose={() => setDeckModal({ open: false, deck: null })}
                onSave={saveDeck}
                initialDeck={deckModal.deck}
            />
            <CardFormModal
                open={cardModal.open}
                onClose={() =>
                    setCardModal({ open: false, card: null, index: null })
                }
                onSave={saveCard}
                initialCard={cardModal.card}
                deck={selectedDeck}
                cards={displayedCards}
                cardIndex={cardModal.index ?? 0}
                navigateCard={(dir) => {
                    if (!displayedCards.length) return
                    const len = displayedCards.length
                    const current = cardModal.index ?? 0
                    const nextIndex =
                        dir === "next"
                            ? (current + 1) % len
                            : (current - 1 + len) % len
                    const nextCard = displayedCards[nextIndex]
                    setCardModal({
                        open: true,
                        card: nextCard,
                        index: nextIndex,
                    })
                }}
            />
            <CsvImportModal
                open={csvImportOpen}
                onClose={() => setCsvImportOpen(false)}
                onImport={importCards}
                deck={selectedDeck}
            />
            <ConfirmDeleteModal
                open={deleteModal.open}
                onClose={() => setDeleteModal({ open: false })}
                onConfirm={
                    deleteModal.type === "deck" ? deleteDeck : deleteCard
                }
                loading={loading.deleting}
                title={`Delete ${deleteModal.type === "deck" ? "Deck" : "Card"}`}
                message={
                    deleteModal.type === "deck"
                        ? `Are you sure? This will permanently delete "${deleteModal.target?.displayName}" and all ${cards.length} card${cards.length !== 1 ? "s" : ""} inside it.`
                        : `Are you sure? This will permanently delete "${deleteModal.target?.name}".`
                }
            />
        </Box>
    )
}

// ── Deck sidebar item ─────────────────────────────────────────────────────────
function DeckItem({ deck, selected, onClick, onEdit, onDelete }) {
    const [hovered, setHovered] = useState(false)
    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={onClick}
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                cursor: "pointer",
                mb: 0.5,
                bgcolor: selected
                    ? "rgba(139,0,0,0.12)"
                    : hovered
                      ? "action.hover"
                      : "transparent",
                border: selected
                    ? "1px solid rgba(139,0,0,0.3)"
                    : "1px solid transparent",
                transition: "all 0.15s",
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.85rem",
                    fontWeight: selected ? "bold" : "normal",
                    fontFamily: '"Cinzel", serif',
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {deck.displayName}
            </Typography>
            {hovered && (
                <Box sx={{ display: "flex", gap: 0, flexShrink: 0, ml: 0.5 }}>
                    <IconButton
                        size='small'
                        onClick={(e) => {
                            e.stopPropagation()
                            onEdit()
                        }}
                        sx={{ p: 0.25, "&:hover": { color: "#8B0000" } }}
                    >
                        <Edit sx={{ fontSize: 12 }} />
                    </IconButton>
                    <IconButton
                        size='small'
                        onClick={(e) => {
                            e.stopPropagation()
                            onDelete()
                        }}
                        sx={{ p: 0.25, "&:hover": { color: "#c62828" } }}
                    >
                        <Delete sx={{ fontSize: 12 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    )
}

// ── Individual admin card tile ────────────────────────────────────────────────
function AdminCard({ card, deckId, deckType, onEdit, onDelete, artCacheBust }) {
    // Prefer artUrl stored on the card record; fall back to convention-based CDN URL
    const baseUrl = card.artUrl || getCardArtUrl(deckId, card.name)
    const artUrl = artCacheBust ? `${baseUrl}?t=${artCacheBust}` : baseUrl
    const rarity = card.rarity || "common"
    const rarityColor = RARITY_COLORS[rarity] || "#777"

    return (
        <Box
            sx={{
                width: 130,
                flexShrink: 0,
                "&:hover .card-actions": { opacity: 1 },
                "&:hover .card-paper": {
                    transform: "translateY(-4px) scale(1.04)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                },
            }}
        >
            <Paper
                className='card-paper'
                elevation={3}
                onClick={onEdit}
                sx={{
                    width: 130,
                    height: 195,
                    borderRadius: "10px",
                    overflow: "hidden",
                    border:
                        deckType === "power"
                            ? `2px solid ${rarityColor}`
                            : "2px solid #8B0000",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    position: "relative",
                    cursor: "pointer",
                }}
            >
                {/* Art */}
                <Box
                    sx={{
                        height: "60%",
                        bgcolor: "#f5f0e6",
                        overflow: "hidden",
                        position: "relative",
                    }}
                >
                    <Box
                        component='img'
                        src={artUrl}
                        alt={card.name}
                        onError={(e) => {
                            e.target.style.display = "none"
                        }}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                    {/* Action overlay */}
                    <Box
                        className='card-actions'
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0,0,0,0.55)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            opacity: 0,
                            transition: "opacity 0.2s",
                        }}
                    >
                        <Tooltip title='Edit'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onEdit()
                                }}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "#1565c0" },
                                }}
                            >
                                <Edit sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                            <IconButton
                                size='small'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onDelete()
                                }}
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "#c62828" },
                                }}
                            >
                                <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                {/* Info */}
                <Box
                    sx={{
                        height: "40%",
                        p: 0.75,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "0.6rem",
                            lineHeight: 1.2,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                        }}
                    >
                        {card.name}
                    </Typography>
                    {deckType === "power" && (
                        <Chip
                            label={rarity}
                            size='small'
                            sx={{
                                height: 12,
                                fontSize: "0.35rem",
                                fontWeight: "bold",
                                bgcolor: rarityColor,
                                color: "#fff",
                                alignSelf: "flex-start",
                            }}
                        />
                    )}
                    {deckType === "monster" && (
                        <Typography
                            sx={{
                                fontSize: "0.5rem",
                                opacity: 0.5,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {card.Attack || "Monster"}
                        </Typography>
                    )}
                </Box>
            </Paper>
        </Box>
    )
}

// ═══════════════════════════════════════════════════════════
// Login Screen
// ═══════════════════════════════════════════════════════════
function LoginScreen({ onSuccess }) {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [challengeSession, setChallengeSession] = useState(null) // string
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")

    const handleLogin = async (e) => {
        e.preventDefault()
        if (!CLIENT_ID) {
            setError(
                "Admin not configured. Set REACT_APP_COGNITO_CLIENT_ID in your environment.",
            )
            return
        }
        setLoading(true)
        setError("")
        try {
            const result = await cognitoSignIn(email.trim(), password)
            if (result.type === "newPasswordRequired") {
                setChallengeSession(result.session)
            } else {
                const { IdToken, AccessToken } = result.tokens
                localStorage.setItem("imk_admin_id_token", IdToken)
                localStorage.setItem("imk_admin_access_token", AccessToken)
                localStorage.setItem("imk_admin_email", email.trim())
                onSuccess(email.trim())
            }
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }

    const handleSetPassword = async (e) => {
        e.preventDefault()
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.")
            return
        }
        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters.")
            return
        }
        setLoading(true)
        setError("")
        try {
            const tokens = await cognitoNewPassword(
                email.trim(),
                challengeSession,
                newPassword,
            )
            const { IdToken, AccessToken } = tokens
            localStorage.setItem("imk_admin_id_token", IdToken)
            localStorage.setItem("imk_admin_access_token", AccessToken)
            localStorage.setItem("imk_admin_email", email.trim())
            onSuccess(email.trim())
        } catch (e) {
            setError(e.message)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background:
                    "linear-gradient(135deg, #0d0606 0%, #1a0a0a 50%, #0d0606 100%)",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Background texture */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.03,
                    backgroundImage:
                        "repeating-linear-gradient(45deg, #fff 0px, #fff 1px, transparent 1px, transparent 50px)",
                }}
            />

            <Paper
                elevation={12}
                sx={{
                    width: { xs: "calc(100vw - 32px)", sm: 400 },
                    maxWidth: 400,
                    borderRadius: "20px",
                    overflow: "hidden",
                    border: "1px solid rgba(139,0,0,0.3)",
                    boxShadow:
                        "0 24px 80px rgba(0,0,0,0.8), 0 0 40px rgba(139,0,0,0.15)",
                }}
            >
                {/* Header */}
                <Box sx={{ p: 3, bgcolor: "#1a0a0a", textAlign: "center" }}>
                    <Box sx={{ mb: 1 }}>
                        <D10Icon size={40} color='#8B0000' />
                    </Box>
                    <Typography
                        sx={{
                            fontFamily: '"Cinzel Decorative", "Cinzel", serif',
                            fontWeight: "bold",
                            fontSize: "1.3rem",
                            color: "#fff",
                            letterSpacing: 2,
                        }}
                    >
                        I MUST KILL
                    </Typography>
                    <Typography
                        sx={{
                            color: "rgba(255,255,255,0.4)",
                            fontSize: "0.75rem",
                            fontFamily: '"Cinzel", serif',
                            letterSpacing: 3,
                            mt: 0.5,
                        }}
                    >
                        {challengeSession ? "SET NEW PASSWORD" : "ADMIN ACCESS"}
                    </Typography>
                </Box>

                {/* Form */}
                <Box
                    component='form'
                    onSubmit={
                        challengeSession ? handleSetPassword : handleLogin
                    }
                    sx={{
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    {error && (
                        <Alert severity='error' onClose={() => setError("")}>
                            {error}
                        </Alert>
                    )}

                    {!challengeSession ? (
                        <>
                            <TextField
                                label='Email'
                                type='email'
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                                required
                                autoFocus
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                            { borderColor: "#8B0000" },
                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            { borderColor: "#8B0000" },
                                    },
                                }}
                            />
                            <TextField
                                label='Password'
                                type='password'
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                fullWidth
                                required
                            />
                        </>
                    ) : (
                        <>
                            <Alert severity='info'>
                                Welcome! Please set a permanent password for
                                your account.
                            </Alert>
                            <TextField
                                label='New Password'
                                type='password'
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                fullWidth
                                required
                                autoFocus
                                helperText='Min. 8 characters with uppercase, lowercase, and numbers'
                            />
                            <TextField
                                label='Confirm Password'
                                type='password'
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                fullWidth
                                required
                            />
                        </>
                    )}

                    <Button
                        type='submit'
                        variant='contained'
                        fullWidth
                        disabled={loading}
                        startIcon={
                            loading ? (
                                <CircularProgress
                                    size={16}
                                    sx={{ color: "#fff" }}
                                />
                            ) : null
                        }
                        sx={{
                            mt: 1,
                            py: 1.5,
                            bgcolor: "#8B0000",
                            "&:hover": { bgcolor: "#a00" },
                            fontFamily: '"Cinzel", serif',
                            letterSpacing: 2,
                            textTransform: "uppercase",
                            fontSize: "0.9rem",
                            borderRadius: "10px",
                        }}
                    >
                        {loading
                            ? "…"
                            : challengeSession
                              ? "Set Password"
                              : "Enter"}
                    </Button>
                </Box>
            </Paper>
        </Box>
    )
}

// ═══════════════════════════════════════════════════════════
// Root Admin Page
// ═══════════════════════════════════════════════════════════
export default function Admin() {
    const [authState, setAuthState] = useState("loading") // loading | unauthenticated | authenticated
    const [email, setEmail] = useState("")

    // Check for existing session on mount
    useEffect(() => {
        const idToken = localStorage.getItem("imk_admin_id_token")
        const savedEmail = localStorage.getItem("imk_admin_email")
        if (idToken && savedEmail) {
            // Basic JWT expiry check (decode without verification)
            try {
                const payload = JSON.parse(atob(idToken.split(".")[1]))
                if (payload.exp * 1000 > Date.now()) {
                    setEmail(savedEmail)
                    setAuthState("authenticated")
                    return
                }
            } catch {
                /* bad token */
            }
            localStorage.removeItem("imk_admin_id_token")
            localStorage.removeItem("imk_admin_access_token")
            localStorage.removeItem("imk_admin_email")
        }
        setAuthState("unauthenticated")
    }, [])

    const handleLoginSuccess = (userEmail) => {
        setEmail(userEmail)
        setAuthState("authenticated")
        // Notify AuthContext in the same tab so EditableSection buttons appear
        window.dispatchEvent(new Event("imk-auth-changed"))
    }

    const handleLogout = async () => {
        const accessToken = localStorage.getItem("imk_admin_access_token")
        if (accessToken) await cognitoSignOut(accessToken)
        localStorage.removeItem("imk_admin_id_token")
        localStorage.removeItem("imk_admin_access_token")
        localStorage.removeItem("imk_admin_email")
        setAuthState("unauthenticated")
        setEmail("")
        window.dispatchEvent(new Event("imk-auth-changed"))
    }

    if (authState === "loading") {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#0d0606",
                }}
            >
                <CircularProgress sx={{ color: "#8B0000" }} />
            </Box>
        )
    }

    if (authState === "unauthenticated") {
        return <LoginScreen onSuccess={handleLoginSuccess} />
    }

    return <AdminDashboard email={email} onLogout={handleLogout} />
}
